<template>
  <div>
    <!-- 预览容器 -->
    <div class="preview-container">
      <!-- 预览头部 -->
      <div class="preview-header">
        <div class="header-left">
          <span class="business-title">{{ formConfig.name }}</span>
          <span v-if="isRevision" class="readonly-reminder">{{ $t('CargoMsg.Cargo_FormPreviewInRevisionViewIsReadOnly') }}</span>
          <span v-if="!isRevision && readonly" class="readonly-reminder">{{ $t('CargoMsg.Cargo_FormPreviewInListViewIsReadOnly') }}</span>
        </div>
        <div class="header-right">
          <!-- 批量模式下显示 '批量填写' -->
          <span v-if="isBatchMode" class="batch-mode-indicator" style="font-size:14px; color: #f57979;">
            {{ $t('CargoMsg.Cargo_BatchFill') }}
          </span>
          <!-- 单任务模式下显示状态和工作号 -->
          <template v-else>
            <span v-if="!debug && jobFull" no="SopState" class="red" style="font-size:14px;">
              {{ ColumnUtils.getJobState(jobFull) }}
            </span>
            <span v-if="!debug && jobFull && jobFull.jobNo" style="color:unset; margin-left: 10px;">
              {{ jobFull.jobNo }}
            </span>
          </template>
        </div>
      </div>

      <!-- 预览内容区域 -->
      <div v-if="!isDataReady" class="loading-container">
        <span class="loading-text">{{ $t('CargoMsg.Cargo_DataLoadingPleaseWait') }}</span>
      </div>
      <div v-else-if="isSe" class="preview-content">
        <!-- 文档选择器 -->
        <div v-if="hasHblElements" class="document-selector">
          <el-select
            v-model="selectedHbl"
            :placeholder="$t('CargoMsg.Cargo_SelectHblDocument')"
            size="small"
            style="width: 300px; margin-right: 10px;"
            value-key="value"
            @change="eManage.updateFormData()"
          >
            <el-option
              v-for="doc in availableHblDocuments"
              :key="doc.key"
              :label="doc.label"
              :value="doc.value"
            />
          </el-select>
        </div>

        <div v-if="hasHawb2Elements" class="document-selector">
          <el-select
            v-model="selectedHawb2"
            :placeholder="$t('CargoMsg.Cargo_SelectHawb2Document')"
            size="small"
            style="width: 300px; margin-right: 10px;"
            value-key="value"
            @change="eManage.updateFormData()"
          >
            <el-option
              v-for="doc in availableHawb2Documents"
              :key="doc.key"
              :label="doc.label"
              :value="doc.value"
            />
          </el-select>
        </div>

        <!-- 遍历页面分块 -->
        <div v-for="pagePart in pageParts" :key="pagePart.id" class="preview-section">
          <!-- 分块标题 -->
          <div class="section-header">
            <span class="section-title">{{ getLocalizedName(pagePart) }}</span>
          </div>

          <div class="section-content">
            <!-- 业务字段分块 -->
            <div v-if="pagePart.blockType === PAGE_PART_TYPE.BUSINESS_ELEMENTS.value" class="element-grid">
              <!-- 遍历元素 -->
              <template v-for="element in pagePart.elements">
                <div
                  v-if="isElementVisible(element) && (eManageGet(element) ? !eManageGet(element, eManage.refreshId).hide : true)"
                  :key="element.uuid"
                  class="element-item"
                  :class="{
                    'has-error': elementErrors[element.elementName],
                    'full-width': showFullWidth(element),
                    'cleared': clearStates[element.elementName]
                  }"
                >
                  <!-- 元素标签 -->
                  <div class="element-label" :class="{ 'required': element.isRequire || element.isDefaultRequire }">{{ getLocalizedName(element) }}</div>

                  <!-- 自定义表单元素组件 -->
                  <!-- NOTE: 目前设计要求元素名唯一，不同页签的同名元素不允许重复添加 -->
                  <component
                    :is="eManageGet(element).component"
                    v-if="eManageGet(element)"
                    :ref="element.elementName"
                    :key="getElementKey(element)"
                    v-bind="eManageGet(element, eManage.refreshId).props"
                    :disabled="isElementDisabled(element) || eManageGet(element, eManage.refreshId).disabled || clearStates[element.elementName]"
                    :value="getFormFieldValueFormatter(element.elementName, element.pageType, job)"
                    :class="{ 'is-error': elementErrors[element.elementName] }"
                    v-on="eManageGet(element, eManage.refreshId).listeners || {}"
                    @input="val => handleElementInput(element, val, eManageGet(element, eManage.refreshId).convertValue)"
                    @change="val => handleElementChange(element, val, eManageGet(element, eManage.refreshId).convertValue)"
                    @childrenChange="data => processElementChange(element, '', data)"
                  />

                  <!-- 日期选择器特殊处理 -->
                  <el-date-picker
                    v-else-if="isDatePicker(element)"
                    :ref="element.elementName"
                    :key="element.elementName"
                    :value="getFormFieldValue(element.elementName, element.pageType, job)"
                    :placeholder="$t('CargoMsg.Cargo_EnterValue')"
                    size="small"
                    v-bind="getElementProps(element, job)"
                    :class="{ 'is-error': elementErrors[element.elementName] }"
                    :disabled="isElementDisabled(element) || clearStates[element.elementName]"
                    @input="(val) => handleElementInput(element, val)"
                    @change="(val) => handleElementChange(element, val)"
                  />

                  <!-- 带星期的日期选择器特殊处理 -->
                  <date-picker-show-week
                    v-else-if="isDatePickerShowWeek(element)"
                    :ref="element.elementName"
                    :key="element.elementName"
                    :value="getFormFieldValue(element.elementName, element.pageType, job)"
                    :placeholder="$t('CargoMsg.Cargo_EnterValue')"
                    size="small"
                    v-bind="getElementProps(element, job)"
                    :class="{ 'is-error': elementErrors[element.elementName] }"
                    :disabled="isElementDisabled(element) || clearStates[element.elementName]"
                    @input="(val) => handleElementInput(element, val)"
                    @change="(val) => handleElementChange(element, val)"
                  />

                  <!-- 箱货信息 -->
                  <template v-else-if="element.elementName === 'seaEq'">
                    <booking-sea-eq-data-grid
                      v-if="job.containerLoadType==='FCL'||job.containerLoadType==='BB'||job.containerLoadType==='BCF'||job.masterFlag"
                      :value="job.seaEQ"
                      :container-load-type="job.containerLoadType"
                      :is-edit="!isElementDisabled(element)"
                      :job="job"
                      @eqQuantityChange="eqQuantityChangeHandler"
                    />
                    <booking-bulk-data-grid
                      v-else-if="job.containerLoadType==='BULK'"
                      :value="job.cargoBulkDetail"
                      :context="context"
                      :job-uuid="job.uuid"
                      :is-edit="!isElementDisabled(element)"
                    />
                    <booking-special-data-grid
                      v-else-if="job.containerLoadType==='SPA'"
                      :value="job.seaEQ"
                      :is-edit="!isElementDisabled(element)"
                      :job="job"
                      @eqQuantityChange="eqQuantityChangeHandler"
                    />
                    <div v-else>
                      <span>{{ $t('CargoMsg.Cargo_None') }}</span>
                    </div>
                  </template>

                  <!-- 形式分单-拆/并单业务关联 -->
                  <template v-else-if="element.pageType === SEA_BOOKING_VIRTUAL_HBL && element.elementName === 'orderBusinessAssociation'">
                    <se-sub-bill-order-business-association
                      :job="job"
                      :cargo-doc-list="job.seaCargoDocs || []"
                      :type-virtual-hbl="job.typeVirtualHbl"
                      :disabled="isElementDisabled(element) || clearStates[element.elementName]"
                    />
                  </template>

                  <!-- 动态元素组件 -->
                  <!-- 设计上考虑过v-model做法，但对于动态组件、内部情况各异的情况，v-model并不合适 -->
                  <!-- 目前仅允许编辑「正操作」状态的业务 -->
                  <component
                    :is="elementComponentMap[element.elementName] || 'el-input'"
                    v-else
                    :ref="element.elementName"
                    :key="element.elementName"
                    :value="getFormFieldValueFormatter(element.elementName, element.pageType, job)"
                    :placeholder="$t('CargoMsg.Cargo_EnterValue')"
                    size="small"
                    v-bind="getElementProps(element, job)"
                    :class="{ 'is-error': elementErrors[element.elementName] }"
                    :disabled="isElementDisabled(element) || clearStates[element.elementName]"
                    @input="(val) => handleElementInput(element, val)"
                    @change="(val) => handleElementChange(element, val)"
                    @childrenChange="data => processElementChange(element, '', data)"
                  >
                    <template v-if="isSelectType(elementComponentMap[element.elementName])">
                      <el-option
                        v-for="(item, index) in getElementSelectOptions(element)"
                        :key="getOptionProp(item, index, element, 'key')"
                        :label="getOptionProp(item, index, element, 'label')"
                        :value="getOptionProp(item, index, element, 'value')"
                      />
                    </template>
                  </component>

                  <!-- 错误信息显示 -->
                  <div v-if="elementErrors[element.elementName]" class="error-message">
                    {{ elementErrors[element.elementName] }}
                  </div>

                  <!-- 批量模式下显示清除数据复选框 -->
                  <div v-if="isBatchMode && !isElementDisabled(element)" class="clear-checkbox-container">
                    <el-checkbox
                      v-model="clearStates[element.elementName]"
                      :disabled="isElementDisabled(element) || (eManageGet(element, eManage.refreshId) && eManageGet(element, eManage.refreshId).disabled)"
                      class="clear-checkbox"
                      @change="handleClearChange(element.elementName, $event)"
                    >
                      {{ $t('CargoMsg.Cargo_ClearData') }}
                    </el-checkbox>
                  </div>
                </div>
              </template>
            </div>

            <!-- 收发通信息分块 -->
            <div v-if="isCsnInfoPagePartType(pagePart.blockType)">
              <!-- 收发通 海出基本页签 -->
              <div v-if="pagePart.blockType === PAGE_PART_TYPE.CSN_INFO_SE_BASIC.value" class="csn-basic-layout">
                <!-- 发货人 -->
                <div v-if="checkCsnFlag(pagePart, 'needShipper')" class="csn-component">
                  <div class="csn-component-header">
                    <div class="csn-component-label">{{ $t('CargoMsg.Cargo_Shipper') }}</div>
                    <!-- 批量模式下显示清除数据复选框 -->
                    <div v-if="isBatchMode && !checkCsnFlag(pagePart, 'isReadonly')" class="clear-checkbox-container">
                      <el-checkbox
                        v-model="clearStates['shipper']"
                        :disabled="checkCsnFlag(pagePart, 'isReadonly')"
                        class="clear-checkbox"
                        @change="handleCsnClearChange('shipper', $event)"
                      >
                        {{ $t('CargoMsg.Cargo_ClearData') }}
                      </el-checkbox>
                    </div>
                  </div>
                  <se-basic-shipper
                    :job="job"
                    :disabled="checkCsnFlag(pagePart, 'isReadonly') || clearStates['shipper']"
                    @childrenChange="handleCsnComponentChange(pagePart, $event)"
                  />
                </div>

                <!-- 收货人 -->
                <div v-if="checkCsnFlag(pagePart, 'needConsignee')" class="csn-component">
                  <div class="csn-component-header">
                    <div class="csn-component-label">{{ $t('CargoMsg.Cargo_Consignee') }}</div>
                    <!-- 批量模式下显示清除数据复选框 -->
                    <div v-if="isBatchMode && !checkCsnFlag(pagePart, 'isReadonly')" class="clear-checkbox-container">
                      <el-checkbox
                        v-model="clearStates['consignee']"
                        :disabled="checkCsnFlag(pagePart, 'isReadonly')"
                        class="clear-checkbox"
                        @change="handleCsnClearChange('consignee', $event)"
                      >
                        {{ $t('CargoMsg.Cargo_ClearData') }}
                      </el-checkbox>
                    </div>
                  </div>
                  <se-basic-consignee
                    :job="job"
                    :disabled="checkCsnFlag(pagePart, 'isReadonly') || clearStates['consignee']"
                    @childrenChange="handleCsnComponentChange(pagePart, $event)"
                  />
                </div>

                <!-- 通知人 -->
                <div v-if="checkCsnFlag(pagePart, 'needNotify')" class="csn-component">
                  <div class="csn-component-header">
                    <div class="csn-component-label">{{ $t('CargoMsg.Cargo_Notify') }}</div>
                    <!-- 批量模式下显示清除数据复选框 -->
                    <div v-if="isBatchMode && !checkCsnFlag(pagePart, 'isReadonly')" class="clear-checkbox-container">
                      <el-checkbox
                        v-model="clearStates['notify']"
                        :disabled="checkCsnFlag(pagePart, 'isReadonly')"
                        class="clear-checkbox"
                        @change="handleCsnClearChange('notify', $event)"
                      >
                        {{ $t('CargoMsg.Cargo_ClearData') }}
                      </el-checkbox>
                    </div>
                  </div>
                  <se-basic-notify
                    :job="job"
                    :disabled="checkCsnFlag(pagePart, 'isReadonly') || clearStates['notify']"
                    @childrenChange="handleCsnComponentChange(pagePart, $event)"
                  />
                </div>

                <!-- 第二通知人 -->
                <div v-if="checkCsnFlag(pagePart, 'needAlsoNotify')" class="csn-component">
                  <div class="csn-component-header">
                    <div class="csn-component-label">{{ $t('SeaExport.SecondNotifyParty') }}</div>
                    <!-- 批量模式下显示清除数据复选框 -->
                    <div v-if="isBatchMode && !checkCsnFlag(pagePart, 'isReadonly')" class="clear-checkbox-container">
                      <el-checkbox
                        v-model="clearStates['alsoNotify']"
                        :disabled="checkCsnFlag(pagePart, 'isReadonly')"
                        class="clear-checkbox"
                        @change="handleCsnClearChange('alsoNotify', $event)"
                      >
                        {{ $t('CargoMsg.Cargo_ClearData') }}
                      </el-checkbox>
                    </div>
                  </div>
                  <cargo-rich-textarea
                    v-model="job.alsoNotify"
                    no="alsoNotify"
                    :disabled="checkCsnFlag(pagePart, 'isReadonly') || clearStates['alsoNotify']"
                    :text-chars="0"
                    wrap-symbol="r"
                    style="width: 310px;height: 230px;"
                    @change="handleAlsoNotifyChange(pagePart, $event)"
                  />
                </div>
              </div>

              <!-- 收发通 海出MBL页签 -->
              <div v-if="pagePart.blockType === PAGE_PART_TYPE.CSN_INFO_SE_MBL.value && seaCargoDocMbl" class="csn-basic-layout">
                <!-- 发货人 -->
                <div v-if="checkCsnFlag(pagePart, 'needShipper')" class="csn-component">
                  <div class="csn-component-header">
                    <div class="csn-component-label">{{ $t('CargoMsg.Cargo_Shipper') }}</div>
                    <!-- 批量模式下显示清除数据复选框 -->
                    <div v-if="isBatchMode && !checkCsnFlag(pagePart, 'isReadonly')" class="clear-checkbox-container">
                      <el-checkbox
                        v-model="clearStates['mblShipper']"
                        :disabled="isEmptyObj(seaCargoDocMbl) || checkCsnFlag(pagePart, 'isReadonly')"
                        class="clear-checkbox"
                        @change="handleCsnClearChange('mblShipper', $event)"
                      >
                        {{ $t('CargoMsg.Cargo_ClearData') }}
                      </el-checkbox>
                    </div>
                  </div>
                  <se-cargo-doc-shipper
                    :cargo-doc="seaCargoDocMbl"
                    :disabled="isEmptyObj(seaCargoDocMbl) || checkCsnFlag(pagePart, 'isReadonly') || clearStates['mblShipper']"
                    @childrenChange="handleCsnComponentChange(pagePart, $event)"
                  />
                </div>

                <!-- 收货人 -->
                <div v-if="checkCsnFlag(pagePart, 'needConsignee')" class="csn-component">
                  <div class="csn-component-header">
                    <div class="csn-component-label">{{ $t('CargoMsg.Cargo_Consignee') }}</div>
                    <!-- 批量模式下显示清除数据复选框 -->
                    <div v-if="isBatchMode && !checkCsnFlag(pagePart, 'isReadonly')" class="clear-checkbox-container">
                      <el-checkbox
                        v-model="clearStates['mblConsignee']"
                        :disabled="isEmptyObj(seaCargoDocMbl) || checkCsnFlag(pagePart, 'isReadonly')"
                        class="clear-checkbox"
                        @change="handleCsnClearChange('mblConsignee', $event)"
                      >
                        {{ $t('CargoMsg.Cargo_ClearData') }}
                      </el-checkbox>
                    </div>
                  </div>
                  <se-cargo-doc-consignee
                    :cargo-doc="seaCargoDocMbl"
                    :disabled="isEmptyObj(seaCargoDocMbl) || checkCsnFlag(pagePart, 'isReadonly') || clearStates['mblConsignee']"
                    @childrenChange="handleCsnComponentChange(pagePart, $event)"
                  />
                </div>

                <!-- 通知人 -->
                <div v-if="checkCsnFlag(pagePart, 'needNotify')" class="csn-component">
                  <div class="csn-component-header">
                    <div class="csn-component-label">{{ $t('CargoMsg.Cargo_Notify') }}</div>
                    <!-- 批量模式下显示清除数据复选框 -->
                    <div v-if="isBatchMode && !checkCsnFlag(pagePart, 'isReadonly')" class="clear-checkbox-container">
                      <el-checkbox
                        v-model="clearStates['mblNotify']"
                        :disabled="isEmptyObj(seaCargoDocMbl) || checkCsnFlag(pagePart, 'isReadonly')"
                        class="clear-checkbox"
                        @change="handleCsnClearChange('mblNotify', $event)"
                      >
                        {{ $t('CargoMsg.Cargo_ClearData') }}
                      </el-checkbox>
                    </div>
                  </div>
                  <se-mbl-notify
                    :cargo-doc="seaCargoDocMbl"
                    :disabled="isEmptyObj(seaCargoDocMbl) || checkCsnFlag(pagePart, 'isReadonly') || clearStates['mblNotify']"
                    @childrenChange="handleCsnComponentChange(pagePart, $event)"
                  />
                </div>
              </div>

              <!-- 收发通 海出HBL页签 -->
              <div v-if="pagePart.blockType === PAGE_PART_TYPE.CSN_INFO_SE_HBL.value && seaCargoDocHbl" class="csn-basic-layout">
                <!-- 发货人 -->
                <div v-if="checkCsnFlag(pagePart, 'needShipper')" class="csn-component">
                  <div class="csn-component-header">
                    <div class="csn-component-label">{{ $t('CargoMsg.Cargo_Shipper') }}</div>
                    <!-- 批量模式下显示清除数据复选框 -->
                    <div v-if="isBatchMode && !checkCsnFlag(pagePart, 'isReadonly')" class="clear-checkbox-container">
                      <el-checkbox
                        v-model="clearStates['hblShipper']"
                        :disabled="isEmptyObj(seaCargoDocHbl) || checkCsnFlag(pagePart, 'isReadonly')"
                        class="clear-checkbox"
                        @change="handleCsnClearChange('hblShipper', $event)"
                      >
                        {{ $t('CargoMsg.Cargo_ClearData') }}
                      </el-checkbox>
                    </div>
                  </div>
                  <se-cargo-doc-shipper
                    :cargo-doc="seaCargoDocHbl"
                    :disabled="isEmptyObj(seaCargoDocHbl) || checkCsnFlag(pagePart, 'isReadonly') || clearStates['hblShipper']"
                    @childrenChange="handleCsnComponentChange(pagePart, $event)"
                  />
                </div>

                <!-- 收货人 -->
                <div v-if="checkCsnFlag(pagePart, 'needConsignee')" class="csn-component">
                  <div class="csn-component-header">
                    <div class="csn-component-label">{{ $t('CargoMsg.Cargo_Consignee') }}</div>
                    <!-- 批量模式下显示清除数据复选框 -->
                    <div v-if="isBatchMode && !checkCsnFlag(pagePart, 'isReadonly')" class="clear-checkbox-container">
                      <el-checkbox
                        v-model="clearStates['hblConsignee']"
                        :disabled="isEmptyObj(seaCargoDocHbl) || checkCsnFlag(pagePart, 'isReadonly')"
                        class="clear-checkbox"
                        @change="handleCsnClearChange('hblConsignee', $event)"
                      >
                        {{ $t('CargoMsg.Cargo_ClearData') }}
                      </el-checkbox>
                    </div>
                  </div>
                  <se-cargo-doc-consignee
                    :cargo-doc="seaCargoDocHbl"
                    :disabled="isEmptyObj(seaCargoDocHbl) || checkCsnFlag(pagePart, 'isReadonly') || clearStates['hblConsignee']"
                    @childrenChange="handleCsnComponentChange(pagePart, $event)"
                  />
                </div>

                <!-- 通知人 -->
                <div v-if="checkCsnFlag(pagePart, 'needNotify')" class="csn-component">
                  <div class="csn-component-header">
                    <div class="csn-component-label">{{ $t('CargoMsg.Cargo_Notify') }}</div>
                    <!-- 批量模式下显示清除数据复选框 -->
                    <div v-if="isBatchMode && !checkCsnFlag(pagePart, 'isReadonly')" class="clear-checkbox-container">
                      <el-checkbox
                        v-model="clearStates['hblNotify']"
                        :disabled="isEmptyObj(seaCargoDocHbl) || checkCsnFlag(pagePart, 'isReadonly')"
                        class="clear-checkbox"
                        @change="handleCsnClearChange('hblNotify', $event)"
                      >
                        {{ $t('CargoMsg.Cargo_ClearData') }}
                      </el-checkbox>
                    </div>
                  </div>
                  <se-hbl-notify
                    :cargo-doc="seaCargoDocHbl"
                    :disabled="isEmptyObj(seaCargoDocHbl) || checkCsnFlag(pagePart, 'isReadonly') || clearStates['hblNotify']"
                    @childrenChange="handleCsnComponentChange(pagePart, $event)"
                  />
                </div>

                <!-- 代理信息 -->
                <div v-if="checkCsnFlag(pagePart, 'needAgentParty')" class="csn-component">
                  <div class="csn-component-label">{{ $t('CargoMsg.Cargo_AgentParty') }}</div>
                  <se-hbl-agent-party
                    :job="job"
                    :cargo-doc="seaCargoDocHbl"
                    :disabled="isEmptyObj(seaCargoDocHbl) || checkCsnFlag(pagePart, 'isReadonly')"
                    @childrenChange="handleCsnComponentChange(pagePart, $event)"
                  />
                </div>

                <!-- 第二通知人 -->
                <div v-if="checkCsnFlag(pagePart, 'needAlsoNotify')" class="csn-component">
                  <div class="csn-component-header">
                    <div class="csn-component-label">ALSO NOTIFY</div>
                    <!-- 批量模式下显示清除数据复选框 -->
                    <div v-if="isBatchMode && !checkCsnFlag(pagePart, 'isReadonly')" class="clear-checkbox-container">
                      <el-checkbox
                        v-model="clearStates['hblAlsoNotify']"
                        :disabled="isEmptyObj(seaCargoDocHbl) || checkCsnFlag(pagePart, 'isReadonly')"
                        class="clear-checkbox"
                        @change="handleCsnClearChange('hblAlsoNotify', $event)"
                      >
                        {{ $t('CargoMsg.Cargo_ClearData') }}
                      </el-checkbox>
                    </div>
                  </div>
                  <cargo-rich-textarea
                    v-model="seaCargoDocHbl.alsoNotify"
                    no="alsoNotify"
                    :disabled="isEmptyObj(seaCargoDocHbl) || checkCsnFlag(pagePart, 'isReadonly') || clearStates['hblAlsoNotify']"
                    :text-chars="0"
                    wrap-symbol="r"
                    style="width: 310px;height: 230px;"
                    @change="handleAlsoNotifyChange(pagePart, $event)"
                  />
                </div>
              </div>

              <!-- 收发通 海出形式分单页签 -->
              <div v-if="pagePart.blockType === PAGE_PART_TYPE.CSN_INFO_SE_HBL2.value && seaCargoDocHawb2" class="csn-basic-layout">
                <!-- 发货人 -->
                <div v-if="checkCsnFlag(pagePart, 'needShipper')" class="csn-component">
                  <div class="csn-component-label">{{ $t('CargoMsg.Cargo_Shipper') }}</div>
                  <se-cargo-doc-shipper
                    :cargo-doc="seaCargoDocHawb2"
                    :disabled="isEmptyObj(seaCargoDocHawb2) || checkCsnFlag(pagePart, 'isReadonly')"
                    @childrenChange="handleCsnComponentChange(pagePart, $event)"
                  />
                </div>

                <!-- 收货人 -->
                <div v-if="checkCsnFlag(pagePart, 'needConsignee')" class="csn-component">
                  <div class="csn-component-label">{{ $t('CargoMsg.Cargo_Consignee') }}</div>
                  <se-cargo-doc-consignee
                    :cargo-doc="seaCargoDocHawb2"
                    :disabled="isEmptyObj(seaCargoDocHawb2) || checkCsnFlag(pagePart, 'isReadonly')"
                    @childrenChange="handleCsnComponentChange(pagePart, $event)"
                  />
                </div>

                <!-- 通知人 -->
                <div v-if="checkCsnFlag(pagePart, 'needNotify')" class="csn-component">
                  <div class="csn-component-label">{{ $t('CargoMsg.Cargo_Notify') }}</div>
                  <se-hbl2-notify
                    :cargo-doc="seaCargoDocHawb2"
                    :disabled="isEmptyObj(seaCargoDocHawb2) || checkCsnFlag(pagePart, 'isReadonly')"
                    @childrenChange="handleCsnComponentChange(pagePart, $event)"
                  />
                </div>

                <!-- 代理信息 -->
                <div v-if="checkCsnFlag(pagePart, 'needAgentParty')" class="csn-component">
                  <div class="csn-component-label">{{ $t('CargoMsg.Cargo_AgentParty') }}</div>
                  <se-hbl2-agent-party
                    :job="job"
                    :cargo-doc="seaCargoDocHawb2"
                    :disabled="isEmptyObj(seaCargoDocHawb2) || checkCsnFlag(pagePart, 'isReadonly')"
                    @childrenChange="handleCsnComponentChange(pagePart, $event)"
                  />
                </div>

                <!-- 第二通知人 -->
                <div v-if="checkCsnFlag(pagePart, 'needAlsoNotify')" class="csn-component">
                  <div class="csn-component-label">ALSO NOTIFY</div>
                  <cargo-rich-textarea
                    v-model="seaCargoDocHawb2.alsoNotify"
                    no="alsoNotify"
                    :disabled="isEmptyObj(seaCargoDocHawb2) || checkCsnFlag(pagePart, 'isReadonly')"
                    :text-chars="0"
                    wrap-symbol="r"
                    style="width: 310px;height: 230px;"
                    @change="handleAlsoNotifyChange(pagePart, $event)"
                  />
                </div>
              </div>
            </div>

            <!-- 文件历史分块 -->
            <div v-if="pagePart.blockType === PAGE_PART_TYPE.UPLOAD_FILE.value" class="file-history-container">
              <tab-file-history
                ref="tabFileHistory"
                :context="context"
                :btn-info="btnInfo"
                :is-cross-tenant-se-to-si="false"
                :cooperate-edit="true"
                :preview-file-method="previewFileMethod"
                :can-operate-job="canOperateJob"
                :view-source="viewSource"
                @writebackJob="handleWritebackJob"
                @mounted="handleComponentMounted('tabFileHistory')"
              />
            </div>

            <!-- 应收费用分块 -->
            <div v-if="pagePart.blockType === PAGE_PART_TYPE.ACCOUNTS_RECEIVABLE.value" class="charges-receivable-container">
              <tab-charges-no-resize
                ref="tabChargesReceivable"
                :context="context"
                :action="action"
                :is-cross-tenant-se-to-si="false"
                :location="location"
                :view-source="viewSource"
                :need-receivable="true"
                :need-payable="false"
                @ChargeLockStatusChange="chargeLockStatusChange"
                @mounted="handleComponentMounted('tabChargesReceivable')"
              />
            </div>

            <!-- 应付费用分块 -->
            <div v-if="pagePart.blockType === PAGE_PART_TYPE.ACCOUNTS_PAYABLE.value" class="charges-payable-container">
              <tab-charges-no-resize
                ref="tabChargesPayable"
                :context="context"
                :action="action"
                :is-cross-tenant-se-to-si="false"
                :location="location"
                :view-source="viewSource"
                :need-receivable="false"
                :need-payable="true"
                @ChargeLockStatusChange="chargeLockStatusChange"
                @mounted="handleComponentMounted('tabChargesPayable')"
              />
            </div>

            <!-- 报关信息分块 -->
            <!-- TODO:
              有个问题，当look模式时，识别、刷新两个按钮可用，edit模式则不可用。
              此处的使用逻辑需要讨论一下，对于分块组件要不要另外处理。
              先按取反处理。
            -->
            <div v-if="pagePart.blockType === PAGE_PART_TYPE.CUSTOMS_INFO.value" class="customs-info-container">
              <tab-customs-info-UICustom
                v-if="$store.state.uiCustom.enable"
                ref="tabCustomsInfo"
                :slave-customs-declaration-enable="slaveCustomsDeclarationEnable"
                :context="context"
                :action="inverseAction"
                :cooperate-edit="true"
                @mounted="handleComponentMounted('tabCustomsInfo')"
              />
              <tab-customs-info
                v-else
                ref="tabCustomsInfo"
                :slave-customs-declaration-enable="slaveCustomsDeclarationEnable"
                :context="context"
                :action="inverseAction"
                :cooperate-edit="true"
                @mounted="handleComponentMounted('tabCustomsInfo')"
              />
            </div>

            <!-- 操作区域分块 -->
            <div v-if="pagePart.blockType === PAGE_PART_TYPE.OPERATION_AREA.value" class="operation-area">
              <!-- HBL打印文档选择下拉框 -->
              <el-select
                v-model="selectedPrintDocument"
                :placeholder="$t('CargoMsg.Cargo_SelectBlDocument')"
                size="small"
                style="width: 260px; margin-right: 10px;"
                value-key="value"
                :clearable="true"
              >
                <el-option
                  v-for="doc in printableHblDocuments"
                  :key="doc.key"
                  :label="doc.label"
                  :value="doc.value"
                />
              </el-select>

              <!-- HBL打印按钮，打印正本或电放 -->
              <el-button
                :disabled="!selectedPrintDocument"
                type="primary"
                @click="originalPrint"
              >
                {{ getHblPrintButtonText() }}
              </el-button>
            </div>

            <!-- 装箱信息分块 -->
            <div v-if="pagePart.blockType === PAGE_PART_TYPE.CONTAINER_INFO.value" class="container-info-container">
              <se-container
                ref="seContainer"
                :job="job"
                :context="context"
                :action="checkDisabled || pagePart.containerConfig.isReadonly ? 'look' : 'edit'"
                :slave-customs-declaration-enable="slaveCustomsDeclarationEnable"
                :master-customs-declaration-enable="masterCustomsDeclarationEnable"
                :container-config="pagePart.containerConfig"
                @mounted="handleComponentMounted('seContainer')"
              />
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="isAe" class="preview-content">
        <!-- 遍历页面分块 -->
        <div v-for="pagePart in pageParts" :key="pagePart.id" class="preview-section">
          <!-- 分块标题 -->
          <div class="section-header">
            <span class="section-title">{{ getLocalizedName(pagePart) }}</span>
          </div>
          <!-- 分块内容 -->
          <div class="section-content">
            <!-- 业务字段分块 -->
            <div v-if="pagePart.blockType === PAGE_PART_TYPE.BUSINESS_ELEMENTS.value" class="element-grid">
              <!-- 遍历元素 -->
              <template v-for="element in pagePart.elements">
                <div
                  v-if="isElementVisible(element) && (eManageGet(element) ? !eManageGet(element, eManage.refreshId).hide : true)"
                  :key="element.uuid"
                  class="element-item"
                  :class="{ 'has-error': elementErrors[element.elementName], 'full-width': showFullWidth(element), 'cleared': clearStates[element.elementName] }"
                >
                  <!-- 元素标签 -->
                  <div class="element-label" :class="{ 'required': element.isRequire || element.isDefaultRequire }">{{ getLocalizedName(element) }}</div>
                  <!-- 元素内容 -->
                  <template>
                    <!-- 自定义表单元素组件 -->
                    <!-- NOTE: 目前设计要求元素名唯一，不同页签的同名元素不允许重复添加 -->
                    <component
                      :is="eManageGet(element).component"
                      v-if="eManageGet(element)"
                      :ref="element.elementName"
                      :key="getElementKey(element)"
                      v-bind="eManageGet(element, eManage.refreshId).props"
                      :disabled="isElementDisabled(element) || eManageGet(element, eManage.refreshId).disabled || clearStates[element.elementName]"
                      :value="getFormFieldValueFormatter(element.elementName, element.pageType, job)"
                      :class="{ 'is-error': elementErrors[element.elementName] }"
                      v-on="eManageGet(element, eManage.refreshId).listeners || {}"
                      @input="(val) => handleElementInput(element, val, eManageGet(element, eManage.refreshId).convertValue)"
                      @change="(val) => handleElementChange(element, val, eManageGet(element, eManage.refreshId).convertValue)"
                      @childrenChange="(data) => processElementChange(element, '', data)"
                    />
                    <!-- 日期选择器特殊处理 -->
                    <el-date-picker
                      v-else-if="isDatePicker(element)"
                      :ref="element.elementName"
                      :key="element.elementName"
                      :value="getFormFieldValue(element.elementName, element.pageType, job)"
                      :placeholder="$t('CargoMsg.Cargo_EnterValue')"
                      size="small"
                      v-bind="getElementProps(element, job)"
                      :class="{ 'is-error': elementErrors[element.elementName] }"
                      :disabled="isElementDisabled(element) || clearStates[element.elementName]"
                      @input="(val) => handleElementInput(element, val)"
                      @change="(val) => handleElementChange(element, val)"
                    />
                    <!-- 带星期的日期选择器特殊处理 -->
                    <date-picker-show-week
                      v-else-if="isDatePickerShowWeek(element)"
                      :ref="element.elementName"
                      :key="element.elementName"
                      :value="getFormFieldValue(element.elementName, element.pageType, job)"
                      :placeholder="$t('CargoMsg.Cargo_EnterValue')"
                      size="small"
                      v-bind="getElementProps(element, job)"
                      :class="{ 'is-error': elementErrors[element.elementName] }"
                      :disabled="isElementDisabled(element) || clearStates[element.elementName]"
                      @input="(val) => handleElementInput(element, val)"
                      @change="(val) => handleElementChange(element, val)"
                    />
                    <!-- 集装器 -->
                    <template v-else-if="element.elementName === 'airUlds'">
                      <booking-air-uld-data-grid
                        v-if="!job.lclMasterUuid && job.operateType !== 0"
                        :value="job.airUld"
                        :is-edit="!isElementDisabled(element) && !clearStates[element.elementName]"
                        :job="job"
                      />
                      <div v-else>
                        <span>{{ $t('CargoMsg.Cargo_None') }}</span>
                      </div>
                    </template>
                    <!-- 动态元素组件 -->
                    <component
                      :is="elementComponentMap[element.elementName] || 'el-input'"
                      v-else
                      :ref="element.elementName"
                      :key="element.elementName"
                      :value="getFormFieldValueFormatter(element.elementName, element.pageType, job)"
                      :placeholder="$t('CargoMsg.Cargo_EnterValue')"
                      size="small"
                      v-bind="getElementProps(element, job)"
                      :class="{ 'is-error': elementErrors[element.elementName] }"
                      :disabled="isElementDisabled(element) || clearStates[element.elementName]"
                      @input="(val) => handleElementInput(element, val)"
                      @change="(val) => handleElementChange(element, val)"
                      @childrenChange="(data) => processElementChange(element, '', data)"
                    >
                      <template v-if="isSelectType(elementComponentMap[element.elementName])">
                        <el-option
                          v-for="(item, index) in getElementSelectOptions(element)"
                          :key="getOptionProp(item, index, element, 'key')"
                          :label="getOptionProp(item, index, element, 'label')"
                          :value="getOptionProp(item, index, element, 'value')"
                        />
                      </template>
                    </component>
                  </template>
                  <!-- 错误信息显示 -->
                  <div v-if="elementErrors[element.elementName]" class="error-message">
                    {{ elementErrors[element.elementName] }}
                  </div>
                  <!-- 批量模式下显示清除数据复选框 -->
                  <div v-if="isBatchMode && !isElementDisabled(element)" class="clear-checkbox-container">
                    <el-checkbox
                      v-model="clearStates[element.elementName]"
                      :disabled="isElementDisabled(element) || (eManageGet(element, eManage.refreshId) && eManageGet(element, eManage.refreshId).disabled)"
                      class="clear-checkbox"
                      @change="handleClearChange(element.elementName, $event)"
                    >
                      {{ $t('CargoMsg.Cargo_ClearData') }}
                    </el-checkbox>
                  </div>
                </div>
              </template>
            </div>
            <!-- 空运货物信息分块 -->
            <div v-if="pagePart.blockType === PAGE_PART_TYPE.AE_CARGO_INFO.value" class="ae-cargo-info-container">
              <ae-basic-goods-info
                ref="aeBasicGoodsInfo"
                :job="job"
                :mawb-doc="aeMawbDoc"
                :disabled="checkDisabled || pagePart.aeCargoInfoConfig.isReadonly"
                :open-imperial="openImperial"
                :show-imperial-button="showImperialButton"
                :display-config="pagePart.aeCargoInfoConfig"
                @childrenChange="(data) => handleComponentChange(pagePart, data)"
                @mawbDocChange="handleAeMawbComponentChange"
              />
            </div>
            <!-- 收发通信息分块 -->
            <div v-if="isCsnInfoPagePartType(pagePart.blockType)">
              <!-- 收发通 空出基本页签 -->
              <div v-if="pagePart.blockType === PAGE_PART_TYPE.CSN_INFO_AE_BASIC.value" class="csn-basic-layout">
                <!-- 发货人 -->
                <div v-if="checkCsnFlag(pagePart, 'needShipper')" class="csn-component">
                  <div class="csn-component-label">{{ $t('CargoMsg.Cargo_Shipper') }}</div>
                  <ae-basic-shipper
                    :job="job"
                    :disabled="checkCsnFlag(pagePart, 'isReadonly')"
                    @childrenChange="handleCsnComponentChange(pagePart, $event)"
                  />
                </div>
                <!-- 收货人 -->
                <div v-if="checkCsnFlag(pagePart, 'needConsignee')" class="csn-component">
                  <div class="csn-component-label">{{ $t('CargoMsg.Cargo_Consignee') }}</div>
                  <ae-basic-consignee
                    :job="job"
                    :disabled="checkCsnFlag(pagePart, 'isReadonly')"
                    @childrenChange="handleCsnComponentChange(pagePart, $event)"
                  />
                </div>
                <!-- 通知人 -->
                <div v-if="checkCsnFlag(pagePart, 'needNotify')" class="csn-component">
                  <div class="csn-component-label">{{ $t('CargoMsg.Cargo_Notify') }}</div>
                  <ae-basic-notify
                    :job="job"
                    :disabled="checkCsnFlag(pagePart, 'isReadonly')"
                    @childrenChange="handleCsnComponentChange(pagePart, $event)"
                  />
                </div>
                <!-- 第二通知人 -->
                <div v-if="checkCsnFlag(pagePart, 'needAlsoNotify')" class="csn-component">
                  <div class="csn-component-label">{{ $t('SeaExport.SecondNotifyParty') }}</div>
                  <cargo-rich-textarea
                    v-model="job.alsoNotify"
                    no="alsoNotify"
                    :disabled="checkCsnFlag(pagePart, 'isReadonly')"
                    :text-chars="0"
                    wrap-symbol="r"
                    style="width: 310px;height: 230px;"
                    @change="handleAlsoNotifyChange(pagePart, $event)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 预览底部操作区 -->
      <div class="preview-footer">
        <el-button v-if="!checkDisabled" type="primary" @click="handleSubmit">{{ $t('CargoMsg.Cargo_Submit') }}</el-button>
        <el-button @click="handleCancel">{{ $t('CargoMsg.Cargo_Cancel') }}</el-button>
      </div>
    </div>

    <!-- 验证对话框 -->
    <ai-custom-page-data-validation
      :visible.sync="showValidationDialog"
      :validation-result="validationResult"
      :page-parts="pageParts"
      @close="handleValidationClose"
    />

    <!-- 打印模板选择对话框 -->
    <cargo-template-selector-dialog
      :visible.sync="templateSelectorDialog.visible"
      :ctx="templateSelectorDialog.ctx"
      :category="templateSelectorDialog.category"
      :data="templateSelectorDialog.data"
      append-to-body
    />
  </div>
</template>

<script>
import {
  BB_CARGO_TYPE,
  BC_CARGO_TYPE,
  LCL_CARGO_TYPE
} from '@/class/cargo/model/parameters/ContainerLoadType'
import { LOG_SE_SEA_BOOKING, LOG_AE_AIR_BOOKING, LOG_BOOKING_CUSTOM_FORM } from '@/class/cargo/model/parameters/SystemConstants'
import * as UICustomConstants from '@/class/cargo/model/parameters/UICustomConstants'
import { UICustomUtil } from '@/class/cargo/model/utils/UICustomUtil'
import { getLocalizedName } from '@/utils/langUtil'
import {
  loadElementComponentDict,
  getElementComponent,
  getElementProps,
  isSelectType,
  getElementSelectOptions,
  getOptionProp,
  getElementComponentInfo
} from '@/store/modules/aiCustomPage/element-registry'
import {
  BUSINESS_TYPE,
  PAGE_PART_TYPE,
  JOB_LIST_NAMES,
  SKIP_JOB_FIELDS,
  COMPARE_JOB_FIELDS,
  CUSTOMS_INFO_JOB_FIELDS
} from '@/class/cargo/model/parameters/AiCustomPageConstants'
import { getBusinessType, isCsnInfoPagePartType, isReadOnlyPagePart, isReadOnlyElement } from '@/class/cargo/model/utils/AiCustomPageUtil'
import { debounce, cloneDeep } from 'lodash'
import i18n from '@/lang'
import { getGuaranteeStateInfo } from '@/api/seaExport/seaCargoDoc'
import { loadSeaExportJobRelated } from '@/api/seaExport/seaExportJob'
import { loadAirExportJobRelated } from '@/api/AirExport/aeBooking'
import { functionRegistry } from '@/store/modules/aiCustomPage/function-registry'
import {
  uiCustomCheckUpdateOrder,
  uiCustomCheckUpdateFields,
  uiCustomUpdateFields,
  uiCustomSave
} from '@/api/sysManage/aiCustomPage'
import AiCustomPageDataValidation from './aiCustomPageDataValidation.vue'
import { cache } from '@/utils/cacheManager'
import { getLoading, getLoading4Cxt } from '@/utils/utilBaseElement'
import ColumnUtils from '@/utils/ColumnUtils'

// 收发通组件导入
import SeBasicShipper from '@/components/uiCustom/se/basic/shipper.vue'
import SeBasicConsignee from '@/components/uiCustom/se/basic/consignee.vue'
import SeBasicNotify from '@/components/uiCustom/se/basic/notify.vue'
import SeCargoDocShipper from '@/components/uiCustom/se/cargoDoc/shipper.vue'
import SeCargoDocConsignee from '@/components/uiCustom/se/cargoDoc/consignee.vue'
import SeMblNotify from '@/components/uiCustom/se/mbl/notify.vue'
import SeHblNotify from '@/components/uiCustom/se/hbl/notify.vue'
import SeHblAgentParty from '@/components/uiCustom/se/hbl/agentParty.vue'
import SeHbl2Notify from '@/components/uiCustom/se/hbl2/notify.vue'
import SeHbl2AgentParty from '@/components/uiCustom/se/hbl2/agentParty.vue'

import AeBasicGoodsInfo from '@/components/uiCustom/ae/basic/goodsInfo.vue'
import AeBasicShipper from '@/components/uiCustom/ae/basic/shipper.vue'
import AeBasicConsignee from '@/components/uiCustom/ae/basic/consignee.vue'
import AeBasicNotify from '@/components/uiCustom/ae/basic/notify.vue'

import DatePickerShowWeek from '@/components/uiCustom/datePickerShowWeek'
import cargoRichTextarea from '@/components/Cargo/Common/cargoRichTextarea'
import TabFileHistory from '@/components/Cargo/Job/booking/tabs/tabFileHistory.vue'
import TabChargesNoResize from '@/components/Cargo/Job/booking/tabs/tabChargesNoResize.vue'
import TabCustomsInfo from '@/components/Cargo/Job/booking/tabs/tabCustomsInfo.vue'
import TabCustomsInfoUICustom from '@/components/Cargo/Job/booking/tabs/tabCustomsInfoUICustom.vue'
import { BookingNewContext } from '@/views/cargo/v0/SeaExport/bookingNewContext'
import { BookingWorkspaceContext } from '@/views/cargo/v0/SeaExport/components/booking/bookingWorkspaceContext'
import { BookingNewContext as AeBookingNewContext } from '@/views/cargo/v0/AirExport/bookingNewContext'
import { BookingWorkspaceContext as AeBookingWorkspaceContext } from '@/views/cargo/v0/AirExport/components/AeBooking/bookingWorkspaceContext'
import cargoTemplateSelectorDialog from '@/components/Cargo/Common/cargoTemplateSelectorDialog'
import bookingSeaEqDataGrid from '@/components/Cargo/Job/bookingSeaEqDataGrid'
import bookingBulkDataGrid from '@/components/Cargo/Job/bookingBulkDataGrid'
import bookingSpecialDataGrid from '@/components/Cargo/Job/bookingSpecialDataGrid'
import SeSubBillOrderBusinessAssociation from '@/components/uiCustom/se/subBill/orderBusinessAssociation.vue'
import SeContainer from '@/components/uiCustom/se/container/index.vue'

import { manualTriggerAndUpate } from '@/api/seaExport/seaExportJob'
import * as AutomaticReleaseConstants from '@/class/cargo/model/parameters/AutomaticReleaseConstants'
import * as SystemConstants from '@/class/cargo/model/parameters/SystemConstants'
import * as BillReleaseType from '@/class/cargo/model/parameters/BillReleaseType'
import { SeaCargoDoc } from '@/class/model/booking/SeaCargoDoc'
import * as CheckRuleConstants from '@/class/cargo/model/parameters/CheckRuleConstants'
import { surrendered, postRemotingTracking } from '@/api/seaExport/seaCargoDoc'
import * as ReleaseState from '@/class/cargo/model/parameters/ReleaseState'
import { SeaExportJob } from '@/class/model/booking/SeaExportJob'
import { AirExportJob } from '@/class/model/booking/AirExportJob'
import { TabError } from '@/utils/class/TabError'
import ChargeUtils from '@/class/cargo/model/utils/ChargeUtils'
import ElementManager from '@/utils/customFormElementManager'
import { TYPE_NAMES, getCacheData, AdvanceSelect } from '@/utils/class/AdvanceSelect'
import { cleanCircleObject } from '@/utils/commonUtil'
import { ContainerUtil } from '@/class/cargo/model/utils/ContainerUtil'
import { JobUtils } from '@/class/cargo/model/utils/JobUtils'
import moment from 'moment'
import BookingAirUldDataGrid from '@/views/cargo/v0/AirExport/components/AeBooking/tabs/components/bookingAirUldDataGrid'
import * as CargoDocConsts from '@/class/cargo/model/parameters/CargoDocConsts'

// 动态代码执行需要，请勿删除
const selectTypeNames = TYPE_NAMES

export default {
  name: 'AiCustomPageDataView',
  i18n,
  components: {
    AiCustomPageDataValidation,
    SeBasicShipper,
    SeBasicConsignee,
    SeBasicNotify,
    SeCargoDocShipper,
    SeCargoDocConsignee,
    SeMblNotify,
    SeHblNotify,
    SeHblAgentParty,
    SeHbl2Notify,
    SeHbl2AgentParty,
    AeBasicGoodsInfo,
    DatePickerShowWeek,
    cargoRichTextarea,
    TabFileHistory,
    TabChargesNoResize,
    TabCustomsInfo,
    TabCustomsInfoUICustom,
    cargoTemplateSelectorDialog,
    bookingSeaEqDataGrid,
    bookingBulkDataGrid,
    bookingSpecialDataGrid,
    SeSubBillOrderBusinessAssociation,
    SeContainer,
    BookingAirUldDataGrid,
    AeBasicShipper,
    AeBasicConsignee,
    AeBasicNotify
  },
  props: {
    // 页面部分数据
    pageParts: {
      type: Array,
      required: true
    },
    // 表单配置
    formConfig: {
      type: Object,
      required: true
    },
    // 表单数据
    formData: {
      type: Object,
      default: null
    },
    // 是否为调试模式
    debug: {
      type: Boolean,
      default: true
    },
    // 是否为只读模式
    readonly: {
      type: Boolean,
      default: false
    },
    // 是否为历史版本
    isRevision: {
      type: Boolean,
      default: false
    },
    // 正式模式的job UUID
    jobUuid: {
      type: String,
      default: null
    },
    // 批量job对象
    jobs: {
      type: Array,
      default: () => []
    },
    // 租户指令数据
    tenantInstructions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    const lob = getBusinessType(this.formConfig.businessType)?.lob || 'SE'
    const isSe = lob === 'SE'
    const isAe = lob === 'AE'
    // 表单数据对象
    let job = null
    if (isSe) {
      job = {
        uuid: null,
        seaEQ: [],
        cargoBulkDetail: [],
        surchargeTerms: [],
        poItems: [],
        // 用于存取提单数据 - 统一使用数组结构
        seaCargoDocs: [],
        // 用于保存费用数据
        charges: []
      }
    } else {
      job = {
        uuid: null,
        airUld: []
      }
    }
    return {
      lob,
      isSe,
      isAe,
      job,
      // 清除状态管理
      clearStates: {},
      // 原始值备份
      originalValues: {},
      // 批量修改提示配置（在组件加载时获取）
      batchModificationPromptConfig: null,
      jobOld: cloneDeep(job),
      // jobFull仅用于只读的查询，不参与编辑
      jobFull: {
        uuid: null
      },
      // 同步API返回的完整job对象（与jobFull结构不同，概念上不混合）
      syncJobFull: {
        uuid: null
      },
      jobState: null,
      formUuid: this.formConfig?.uuid,
      // 元素错误信息存储
      elementErrors: {},
      // 防抖的验证函数
      debouncedValidate: null,
      // 防抖的元素变更处理函数
      debouncedProcessElementChange: null,
      // 是否正在更新值
      isUpdating: false,
      // 元素组件映射表
      elementComponentMap: {},
      // 记录最后触发同步的字段名（用于避免循环更新）
      lastSyncElementName: null,
      // 收发通大小写控制
      notUppercaseShipperConsigneNotify: true,
      // 数据是否已准备就绪
      isDataReady: false,
      // 防止递归同步API调用的标志
      isSyncing: false,

      // 文档选择状态
      selectedHbl: null, // 当前选中的HBL/HBL拆单文档
      selectedHawb2: null, // 当前选中的形式分单文档

      // 常量引入
      PAGE_PART_TYPE,
      JOB_LIST_NAMES,
      SKIP_JOB_FIELDS,
      COMPARE_JOB_FIELDS,
      CUSTOMS_INFO_JOB_FIELDS,
      SEA_BOOKING_VIRTUAL_HBL: UICustomConstants.SEA_BOOKING_VIRTUAL_HBL,

      showValidationDialog: false,
      saveAfterValidate: false,
      validationResult: null,
      validationRequest: null,
      // 费用数据是否变化
      isChargeChanges: false,
      loading: null,
      ColumnUtils,
      // 业务上下文信息，其中包含各组件需要的配置和基础信息
      context: null,
      // 海出基础上下文（BookingNewContext）
      baseSeaExportContext: null,
      // 海出工作区上下文（BookingWorkspaceContext）
      seaExportContext: null,
      // 空出基础上下文（AeBookingNewContext）
      baseAirExportContext: null,
      // 空出工作区上下文（AeBookingWorkspaceContext）
      airExportContext: null,
      showImperialButton: false, // 英制单位显示按钮
      openImperial: false, // 是否开启英制单位
      // 预览文件参数
      previewFileData: {
        visible: false,
        pdfUrl: '',
        pdfSrc: '',
        numPages: '',
        photoList: [],
        officeFileUrl: '',
        showViewBtn: false
      },
      // 部分特殊组件的mounted事件等待延迟
      COMPONENT_MOUNTED_DELAY: 300,
      // 保函相关状态
      guaranteeConfigEnable: false, // 是否启用保函校验
      singleTicketGuaranteeState: 0, // 单票保函状态
      longTermGuaranteeState: 0, // 长期保函状态
      // 打印模板选择对话框状态
      templateSelectorDialog: {
        visible: false,
        ctx: null,
        category: '',
        data: {}
      },
      // 打印相关回调
      prePrintCallback: null,
      printedCallback: null,
      v2CheckCallback: null,
      returnMainCallback: null,
      originalPrintCheckSerialNo: false, // 是否启用提单流水号打印验证
      selectedPrintDocument: null,
      eManage: new ElementManager(job, pageType => this.getPageFormData(pageType, '')),
      aiContext: {
        labels: {},
        options: {}
      },
      config: {
        syncSignDateBl: false,
        seMultiSoMode: false // 海运出口开启拼箱小票多SO操作模式
      },
      needMergeDataBeforeSave: {} // 保存前需要合并的数据
    }
  },
  computed: {
    // 判断是否为批量模式
    isBatchMode() {
      return this.jobs && this.jobs.length > 1
    },
    editableJobStates() {
      if (this.formConfig.outputParams && this.formConfig.outputParams.jobState) {
        const jobStates = [...this.formConfig.outputParams.jobState]
        if (!jobStates.includes(SystemConstants.STATE_OPERATING.code)) {
          jobStates.push(SystemConstants.STATE_OPERATING.code)
        }
        return jobStates
      }
      return [SystemConstants.STATE_OPERATING.code]
    },
    validJobState() {
      return this.debug || (this.jobFull.jobNo && this.editableJobStates.includes(this.jobState))
    },
    checkDisabled() {
      return this.readonly || this.isRevision || (!this.isBatchMode && !this.validJobState)
    },
    // 元素映射表，以elementName为key
    elementMap() {
      const map = {}
      for (const pagePart of this.pageParts) {
        for (const element of pagePart.elements) {
          map[element.elementName] = element
        }
      }
      return map
    },
    seaCargoDocMbl() {
      return this.findJobSeaCargoDoc(this.job, 'MBL')
    },
    seaCargoDocHbl() {
      return this.findJobSeaCargoDoc(this.job, 'HBL', this.selectedHbl)
    },
    seaCargoDocHawb2() {
      return this.findJobSeaCargoDoc(this.job, 'HAWB2', this.selectedHawb2)
    },
    btnInfo() {
      return {
        label: {
          shutOutLabel: this.$t('SeaExport.ShutOut'),
          btnEntrustment: this.$t('SeaExport.Consignation'), // 内部委托按钮
          btnEntrustmentSI: this.$t('SeaExport.ConsignationSI') // 出口转进口按钮
        },
        btnEntrustmentSISynchronized: false, // 出口转进口（已同步过）
        visible: {
          sendBackBookingButton: true, // 发送订舱数据
          printFclProfit: true, // 整箱利润打印
          printLclProfit: true, // 拼箱利润打印
          cancelReturn: true, // 退回操作
          printReturn: true // 撤回
        },
        privilege: {
          btnEntrustment: null, // 委托按钮是否禁用
          btnShutout: false, // 退关按钮是否可用
          consignationEnable: false, // 内部委托按钮是否可用
          consignationEnableSI: false, // 出口转进口按钮是否可用
          consignationCrossEnable: false, // 跨分公司委托是否可用
          crossMoreSign: false,
          button_SEBooking_cancelShutout: this.$checkPermission(['button_SEBooking_cancelShutout']),
          button_SEBooking_Shutout: this.$checkPermission(['button_SEBooking_Shutout']),
          button_JobBooking_Submit: this.$checkPermission(['button_JobBooking_Submit']),
          button_JobBooking_PreSubmit: this.$checkPermission(['button_JobBooking_PreSubmit']),
          button_JobBooking_ManagerSubmit: this.$checkPermission(['button_JobBooking_ManagerSubmit']),
          button_JobBooking_ManagerAuditSubmit: this.$checkPermission(['button_JobBooking_ManagerAuditSubmit']),
          button_JobBooking_PrintProfit: this.$checkPermission(['button_JobBooking_PrintProfit']),
          button_SEBooking_cancelProfit: this.$checkPermission(['button_SEBooking_cancelProfit']),
          button_JobBooking_Retract: this.$checkPermission(['button_JobBooking_Retract'])
        }
      }
    },
    canOperateJob() {
      if (this.jobFull.jobNo) {
        return this.matchOwnerBranch(this.jobFull)
      }
      return true
    },
    viewSource() {
      if (this.formConfig.businessType === BUSINESS_TYPE.SEA_EXPORT.value) {
        // 海运出口
        return 'SeBooking'
      } else if (this.formConfig.businessType === BUSINESS_TYPE.AIR_EXPORT.value) {
        // 空运出口
        return 'AeBooking'
      } else {
        // 默认取值
        return 'SeBooking'
      }
    },
    location() {
      if (this.formConfig.businessType === BUSINESS_TYPE.AIR_EXPORT.value) {
        return LOG_AE_AIR_BOOKING.code
      }
      return LOG_SE_SEA_BOOKING.code
    },
    action() {
      if (this.checkDisabled) {
        return 'look'
      }
      return 'edit'
    },
    inverseAction() {
      return this.action === 'look' ? 'edit' : 'look'
    },
    slaveCustomsDeclarationEnable() { // 【小票】关单信息 是否可用
      if (this.debug || this.jobFull.jobNo) {
        return false
      }
      return this.jobFull.lob === SystemConstants.SE.code &&
        !this.jobFull.masterFlag && this.config.seMultiSoMode && [LCL_CARGO_TYPE.code, BB_CARGO_TYPE.code, BC_CARGO_TYPE.code].indexOf(this.job.containerLoadType) >= 0
    },
    masterCustomsDeclarationEnable() { // 【主票】关单信息 是否可用
      return this.jobFull.lob === SystemConstants.SE.code && this.jobFull.masterFlag && this.config.seMultiSoMode && [LCL_CARGO_TYPE.code, BC_CARGO_TYPE.code].indexOf(this.job.containerLoadType) >= 0
    },
    // 可打印HBL文档列表
    printableHblDocuments() {
      const docs = []

      // 检查是否有HBL文档
      const hblDoc = this.findSeaCargoDoc(this.job, 'HBL')
      if (hblDoc) {
        docs.push({
          key: hblDoc.uuid,
          label: this.getBlDocumentLabel(hblDoc),
          value: hblDoc.uuid
        })
      }

      // 检查是否有HBL拆单文档
      const hbl2Docs = this.findAllSeaCargoDoc(this.job, 'HBL2')
      hbl2Docs.forEach((doc, index) => {
        docs.push({
          key: doc.uuid,
          label: this.getBlDocumentLabel(doc),
          value: doc.uuid
        })
      })

      // 检查是否有形式分单文档
      const hawb2Docs = this.findAllSeaCargoDoc(this.job, 'HAWB2')
      hawb2Docs.forEach((doc, index) => {
        docs.push({
          key: doc.uuid,
          label: this.getBlDocumentLabel(doc),
          value: doc.uuid
        })
      })

      console.log('[调试] 可打印HBL文档列表', docs)

      return docs
    },
    // 检查是否有HBL/HBL拆单元素
    hasHblElements() {
      return this.pageParts.some(pagePart =>
        pagePart.blockType === PAGE_PART_TYPE.CSN_INFO_SE_HBL.value ||
        (pagePart.elements && pagePart.elements.some(element =>
          element.pageType === UICustomConstants.SEA_BOOKING_HBL
        ))
      )
    },
    // 检查是否有形式分单元素
    hasHawb2Elements() {
      return this.pageParts.some(pagePart =>
        pagePart.blockType === PAGE_PART_TYPE.CSN_INFO_SE_HBL2.value ||
        (pagePart.elements && pagePart.elements.some(element =>
          element.pageType === UICustomConstants.SEA_BOOKING_VIRTUAL_HBL
        ))
      )
    },
    // 可用的HBL + HBL拆单文档列表
    availableHblDocuments() {
      const docs = []

      // 检查是否有HBL文档
      const hblDoc = this.findSeaCargoDoc(this.job, 'HBL')
      if (hblDoc && hblDoc.hblNbr2) {
        docs.push({
          key: hblDoc.uuid,
          label: this.getBlDocumentLabel(hblDoc),
          value: hblDoc.uuid
        })
      }

      // 检查是否有HBL拆单文档
      const hbl2Docs = this.findAllSeaCargoDoc(this.job, 'HBL2')
      hbl2Docs.forEach((doc) => {
        if (doc.hblNbr2) {
          docs.push({
            key: doc.uuid,
            label: this.getBlDocumentLabel(doc),
            value: doc.uuid
          })
        }
      })

      return docs.sort((a, b) => a.label.localeCompare(b.label))
    },
    // 可用的形式分单文档列表
    availableHawb2Documents() {
      const docs = []

      // 检查是否有形式分单文档
      const hawb2Docs = this.findAllSeaCargoDoc(this.job, 'HAWB2')
      hawb2Docs.forEach((doc) => {
        docs.push({
          key: doc.uuid,
          label: this.getBlDocumentLabel(doc),
          value: doc.uuid
        })
      })

      return docs.sort((a, b) => a.label.localeCompare(b.label))
    },
    aeMawbDoc() {
      return this.findAeDoc(this.job, CargoDocConsts.MAWB) || {}
    }
  },
  watch: {
    // 监听HBL文档选择变化
    selectedHbl(newValue, oldValue) {
      if (newValue !== oldValue && this.isDataReady) {
        console.log('[调试] HBL文档选择变化', { oldValue, newValue })
        this.reloadElements(UICustomConstants.SEA_BOOKING_HBL)
      }
    },
    // 监听HAWB2文档选择变化
    selectedHawb2(newValue, oldValue) {
      if (newValue !== oldValue && this.isDataReady) {
        console.log('[调试] HAWB2文档选择变化', { oldValue, newValue })
        this.reloadElements(UICustomConstants.SEA_BOOKING_VIRTUAL_HBL)
      }
    }
  },
  async created() {
    // 创建防抖的验证函数
    this.debouncedValidate = debounce(this.validateAllElements, 300)
    // 创建防抖的元素变更处理函数
    this.debouncedProcessElementChange = debounce(this.processElementChange, 300)
  },
  async mounted() {
    // 初始化上下文
    await this.initializeContexts()

    this.initConfig()

    await Promise.all([
      this.initializeJobData(), // 初始化表单数据
      this.initializeUICustomPageInfo() // 初始化UI自定义配置信息
    ])

    // 确保数据准备好后再执行组件映射
    console.log('[数据视图] 组件挂载开始')
    await this.initializeComponentMaps()

    // 预先获取批量修改提示配置
    if (this.isBatchMode && this.formConfig?.uuid) {
      this.loadBatchModificationPromptConfig()
    }

    console.log('[数据视图] 组件挂载完成')

    // 设置数据已准备就绪
    this.isDataReady = true

    // 初始化特定业务类型的状态信息
    await this.initializeBusinessStates()

    // 初始化文档选择
    this.initializeDocumentSelections()

    // 初始化表单代码和规则
    this.initializeFormCode()
  },
  beforeDestroy() {
    // 组件销毁前取消未执行的防抖函数
    if (this.debouncedValidate && this.debouncedValidate.cancel) {
      this.debouncedValidate.cancel()
    }
    if (this.debouncedProcessElementChange && this.debouncedProcessElementChange.cancel) {
      this.debouncedProcessElementChange.cancel()
    }
  },
  methods: {
    // 处理清除复选框状态变更
    handleClearChange(fieldName, isCleared) {
      if (isCleared) {
        // 检查是否为多字段组件
        if (this.isMultiFieldComponent(fieldName)) {
          // 处理多字段组件：清空所有子字段
          const subFields = this.getMultiFieldSubFields(fieldName)
          subFields.forEach(subField => {
            this.originalValues[`${fieldName}_${subField}`] = this.job[subField]
            this.job[subField] = null // 多字段组件的子字段都是数值类型，设为null
            // 重要：只在批量模式下将子字段标记为被清除状态
            if (this.isBatchMode) {
              this.$set(this.clearStates, subField, true)
            }
          })
        } else {
          // 处理单字段组件
          this.originalValues[fieldName] = this.job[fieldName]
          // 根据字段类型设置不同的清空值
          if (this.isDateField(fieldName) || this.isNumberField(fieldName)) {
            this.job[fieldName] = null // 日期字段和数值字段清空时设为 null
          } else {
            this.job[fieldName] = '' // 其他字段清空时设为空字符串
          }
        }
      } else {
        // 恢复原值
        if (this.isMultiFieldComponent(fieldName)) {
          // 恢复多字段组件的子字段
          const subFields = this.getMultiFieldSubFields(fieldName)
          subFields.forEach(subField => {
            const originalKey = `${fieldName}_${subField}`
            this.job[subField] = this.originalValues[originalKey] || null
            // 只在批量模式下清除子字段的清空状态
            if (this.isBatchMode) {
              this.$set(this.clearStates, subField, false)
            }
          })
        } else {
          // 恢复单字段组件
          const defaultValue = (this.isDateField(fieldName) || this.isNumberField(fieldName)) ? null : ''
          this.job[fieldName] = this.originalValues[fieldName] || defaultValue
        }
      }
    },

    // 处理收发通信息组件的清除数据
    handleCsnClearChange(componentType, isCleared) {
      const clearFields = this.getCsnClearFields(componentType)

      if (isCleared) {
        // 备份原值并清空相关字段
        clearFields.forEach(field => {
          const { target, fieldName } = field
          if (!this.originalValues[`${componentType}_${fieldName}`]) {
            this.originalValues[`${componentType}_${fieldName}`] = target[fieldName]
          }
          target[fieldName] = ''
        })
      } else {
        // 恢复原值
        clearFields.forEach(field => {
          const { target, fieldName } = field
          const originalKey = `${componentType}_${fieldName}`
          if (this.originalValues[originalKey] !== undefined) {
            target[fieldName] = this.originalValues[originalKey]
          }
        })
      }

      // 强制更新视图以触发置灰效果
      this.$forceUpdate()
      console.log(`[清除数据] 收发通信息组件 ${componentType} 清除状态变更为: ${isCleared}`)
    },

    // 获取收发通信息组件需要清除的字段
    getCsnClearFields(componentType) {
      const fields = []

      // 基本页签的收发通字段
      if (componentType === 'shipper') {
        return [
          { target: this.job, fieldName: 'shipperCode' },
          { target: this.job, fieldName: 'shipperId' },
          ...Array.from({ length: 7 }, (_, i) => ({ target: this.job, fieldName: `shipperLine${i + 1}` }))
        ]
      }

      if (componentType === 'consignee') {
        return [
          { target: this.job, fieldName: 'consigneeCode' },
          { target: this.job, fieldName: 'consigneeId' },
          ...Array.from({ length: 7 }, (_, i) => ({ target: this.job, fieldName: `consigneeLine${i + 1}` }))
        ]
      }

      if (componentType === 'notify') {
        return [
          { target: this.job, fieldName: 'notifyCode' },
          { target: this.job, fieldName: 'notifyId' },
          ...Array.from({ length: 7 }, (_, i) => ({ target: this.job, fieldName: `notifyLine${i + 1}` }))
        ]
      }

      if (componentType === 'alsoNotify') {
        return [{ target: this.job, fieldName: 'alsoNotify' }]
      }

      // MBL页签的收发通字段
      if (componentType === 'mblShipper' && this.seaCargoDocMbl) {
        return [
          { target: this.seaCargoDocMbl, fieldName: 'shipperCode' },
          { target: this.seaCargoDocMbl, fieldName: 'shipperId' },
          ...Array.from({ length: 7 }, (_, i) => ({ target: this.seaCargoDocMbl, fieldName: `shipperLine${i + 1}` }))
        ]
      }

      if (componentType === 'mblConsignee' && this.seaCargoDocMbl) {
        return [
          { target: this.seaCargoDocMbl, fieldName: 'consigneeCode' },
          { target: this.seaCargoDocMbl, fieldName: 'consigneeId' },
          ...Array.from({ length: 7 }, (_, i) => ({ target: this.seaCargoDocMbl, fieldName: `consigneeLine${i + 1}` }))
        ]
      }

      if (componentType === 'mblNotify' && this.seaCargoDocMbl) {
        return [
          { target: this.seaCargoDocMbl, fieldName: 'notifyCode' },
          { target: this.seaCargoDocMbl, fieldName: 'notifyId' },
          ...Array.from({ length: 7 }, (_, i) => ({ target: this.seaCargoDocMbl, fieldName: `notifyLine${i + 1}` }))
        ]
      }

      // HBL页签的收发通字段
      if (componentType === 'hblShipper' && this.seaCargoDocHbl) {
        return [
          { target: this.seaCargoDocHbl, fieldName: 'shipperCode' },
          { target: this.seaCargoDocHbl, fieldName: 'shipperId' },
          ...Array.from({ length: 7 }, (_, i) => ({ target: this.seaCargoDocHbl, fieldName: `shipperLine${i + 1}` }))
        ]
      }

      if (componentType === 'hblConsignee' && this.seaCargoDocHbl) {
        return [
          { target: this.seaCargoDocHbl, fieldName: 'consigneeCode' },
          { target: this.seaCargoDocHbl, fieldName: 'consigneeId' },
          ...Array.from({ length: 7 }, (_, i) => ({ target: this.seaCargoDocHbl, fieldName: `consigneeLine${i + 1}` }))
        ]
      }

      if (componentType === 'hblNotify' && this.seaCargoDocHbl) {
        return [
          { target: this.seaCargoDocHbl, fieldName: 'notifyCode' },
          { target: this.seaCargoDocHbl, fieldName: 'notifyId' },
          ...Array.from({ length: 7 }, (_, i) => ({ target: this.seaCargoDocHbl, fieldName: `notifyLine${i + 1}` }))
        ]
      }

      if (componentType === 'hblAlsoNotify' && this.seaCargoDocHbl) {
        return [{ target: this.seaCargoDocHbl, fieldName: 'alsoNotify' }]
      }

      return fields
    },

    // 添加收发通信息组件的修改字段到批量修改列表
    addCsnModifyFields(modifyFields, startIndex) {
      let index = startIndex

      // 检查基本页签收发通字段
      const csnFields = ['shipper', 'consignee', 'notify', 'alsoNotify']
      csnFields.forEach(csnType => {
        if (this.clearStates[csnType] === true) {
          // 被清除的收发通组件，添加相关字段为空字符串
          const fieldNames = this.getCsnFieldNames(csnType)
          fieldNames.forEach(fieldName => {
            modifyFields.push({
              filed: fieldName,
              value: '',
              sort: index++
            })
            console.log(`[批量调试] 收发通组件 ${csnType} 被清除，字段 ${fieldName} 设为空字符串`)
          })
        } else {
          // 检查收发通组件的实际数据变更
          const fieldNames = this.getCsnFieldNames(csnType)
          fieldNames.forEach(fieldName => {
            const newValue = this.job[fieldName]
            const oldValue = this.jobOld[fieldName]
            if (newValue && newValue !== oldValue) {
              modifyFields.push({
                filed: fieldName,
                value: newValue,
                sort: index++
              })
              console.log(`[批量调试] 收发通字段修改: ${fieldName}, 新值:`, newValue)
            }
          })
        }
      })

      // 检查MBL收发通字段
      const mblCsnFields = ['mblShipper', 'mblConsignee', 'mblNotify']
      mblCsnFields.forEach(csnType => {
        if (this.clearStates[csnType] === true) {
          // 被清除的MBL收发通组件
          const fieldNames = this.getCsnFieldNames(csnType)
          fieldNames.forEach(fieldName => {
            modifyFields.push({
              filed: `mbl.${fieldName}`,
              value: '',
              sort: index++
            })
            console.log(`[批量调试] MBL收发通组件 ${csnType} 被清除，字段 mbl.${fieldName} 设为空字符串`)
          })
        } else if (this.seaCargoDocMbl) {
          // 检查MBL收发通组件的实际数据变更
          const fieldNames = this.getCsnFieldNames(csnType)
          fieldNames.forEach(fieldName => {
            const newValue = this.seaCargoDocMbl[fieldName]
            const oldValue = this.jobOld.seaCargoDocs?.find(doc => doc.docType === 'MBL')?.[fieldName]
            if (newValue && newValue !== oldValue) {
              modifyFields.push({
                filed: `mbl.${fieldName}`,
                value: newValue,
                sort: index++
              })
              console.log(`[批量调试] MBL收发通字段修改: mbl.${fieldName}, 新值:`, newValue)
            }
          })
        }
      })

      // 检查HBL收发通字段
      const hblCsnFields = ['hblShipper', 'hblConsignee', 'hblNotify', 'hblAlsoNotify']
      hblCsnFields.forEach(csnType => {
        if (this.clearStates[csnType] === true) {
          // 被清除的HBL收发通组件
          const fieldNames = this.getCsnFieldNames(csnType)
          fieldNames.forEach(fieldName => {
            modifyFields.push({
              filed: `hbl.${fieldName}`,
              value: '',
              sort: index++
            })
            console.log(`[批量调试] HBL收发通组件 ${csnType} 被清除，字段 hbl.${fieldName} 设为空字符串`)
          })
        } else if (this.seaCargoDocHbl) {
          // 检查HBL收发通组件的实际数据变更
          const fieldNames = this.getCsnFieldNames(csnType)
          fieldNames.forEach(fieldName => {
            const newValue = this.seaCargoDocHbl[fieldName]
            const oldValue = this.jobOld.seaCargoDocs?.find(doc => doc.docType === 'HBL')?.[fieldName]
            if (newValue && newValue !== oldValue) {
              modifyFields.push({
                filed: `hbl.${fieldName}`,
                value: newValue,
                sort: index++
              })
              console.log(`[批量调试] HBL收发通字段修改: hbl.${fieldName}, 新值:`, newValue)
            }
          })
        }
      })
    },

    // 获取收发通组件对应的字段名列表
    getCsnFieldNames(csnType) {
      const baseType = csnType.replace(/^(mbl|hbl)/, '')

      if (baseType === 'Shipper') {
        return ['shipperCode', 'shipperId', 'shipperLine1', 'shipperLine2', 'shipperLine3', 'shipperLine4', 'shipperLine5', 'shipperLine6', 'shipperLine7']
      }

      if (baseType === 'Consignee') {
        return ['consigneeCode', 'consigneeId', 'consigneeLine1', 'consigneeLine2', 'consigneeLine3', 'consigneeLine4', 'consigneeLine5', 'consigneeLine6', 'consigneeLine7']
      }

      if (baseType === 'Notify') {
        return ['notifyCode', 'notifyId', 'notifyLine1', 'notifyLine2', 'notifyLine3', 'notifyLine4', 'notifyLine5', 'notifyLine6', 'notifyLine7']
      }

      if (baseType === 'AlsoNotify') {
        return ['alsoNotify']
      }

      // 基本页签的收发通字段（不带前缀）
      if (csnType === 'shipper') {
        return ['shipperCode', 'shipperId', 'shipperLine1', 'shipperLine2', 'shipperLine3', 'shipperLine4', 'shipperLine5', 'shipperLine6', 'shipperLine7']
      }

      if (csnType === 'consignee') {
        return ['consigneeCode', 'consigneeId', 'consigneeLine1', 'consigneeLine2', 'consigneeLine3', 'consigneeLine4', 'consigneeLine5', 'consigneeLine6', 'consigneeLine7']
      }

      if (csnType === 'notify') {
        return ['notifyCode', 'notifyId', 'notifyLine1', 'notifyLine2', 'notifyLine3', 'notifyLine4', 'notifyLine5', 'notifyLine6', 'notifyLine7']
      }

      if (csnType === 'alsoNotify') {
        return ['alsoNotify']
      }

      return []
    },

    eManageGet(element, refreshId) {
      return this.eManage.get(element.pageType, element.elementName, refreshId)
    },
    getElementKey(element) {
      return `${element.pageType}-${element.elementName}`
    },
    isEmptyObj(val) {
      return typeof val === 'object' && Object.keys(val).length === 0
    },
    // 初始化表单代码和规则
    initializeFormCode() {
      // 清除函数注册表中的所有规则
      functionRegistry.clearFormFunctions(this.formUuid)

      // 初始化表单代码
      const formCode = this.formConfig.formCode || ''

      // 修复单引号转义问题：将 \' 替换为 '
      let processedFormCode = formCode
      if (processedFormCode) {
        processedFormCode = processedFormCode.replace(/\\'/g, "'")
      }

      if (!processedFormCode) return

      console.log('[数据视图] 开始解析表单代码中的规则...')
      try {
        // 使用正则表达式匹配规则块，支持多行JSON
        const ruleRegex = /<!--\s*\n\s*({[\s\S]*?})\s*\n\s*-->\s*\n\s*<script>\s*\n([\s\S]*?)<\/script>/g
        let match
        const rules = []

        while ((match = ruleRegex.exec(processedFormCode)) !== null) {
          const jsonContent = match[1].trim()
          const functionCode = match[2].trim()
          console.log('[数据视图] 匹配规则JSON元信息:', jsonContent)
          console.log('[数据视图] 匹配函数代码:', functionCode)

          // 尝试解析JSON数据
          try {
            console.log('[数据视图] 解析JSON数据:', jsonContent)
            const ruleData = JSON.parse(jsonContent)

            if (ruleData && ruleData.id) {
              console.log(`[数据视图] 加载规则: ${ruleData.id} (${ruleData.type})`)
              // 使用script中的函数代码，这个更方便编辑
              ruleData.code = functionCode
              rules.push(ruleData)
              // 注册到函数注册表
              functionRegistry.registerFunction(
                this.formUuid,
                ruleData.id,
                ruleData.code,
                ruleData.type,
                ruleData.metadata
              )
            }
          } catch (jsonError) {
            console.error('[数据视图] JSON解析错误:', jsonError)
          }
        }

        console.log(`[数据视图] 规则加载完成，共 ${rules.length} 条规则`)
      } catch (error) {
        console.error('[数据视图] 解析规则失败:', error)
      }
    },
    // 获取本地化名称
    getLocalizedName(data) {
      return getLocalizedName(data)
    },
    // 获取页签表单数据
    getPageFormData(pageType, fieldName = '', job) {
      if (!job) {
        job = this.job
      }
      if (this.isSe) {
        const docType = this.getDocTypeByPageType(pageType, fieldName)
        if (docType === 'MBL') {
          return job.seaCargoDocs.find(doc => doc.docType === 'MBL') || null
        } else if (docType === 'HBL' || docType === 'HBL2') {
          // 批量模式下直接返回第一个HBL文档（用于值设置）
          if (this.isBatchMode) {
            return job.seaCargoDocs.find(doc => doc.docType === 'HBL' || doc.docType === 'HBL2') || null
          } else {
            // 单票模式下使用选中的HBL文档
            return job.seaCargoDocs.find(doc => (doc.docType === 'HBL' || doc.docType === 'HBL2') && doc.uuid === this.selectedHbl) || null
          }
        } else if (docType === 'HAWB2') {
          // 使用选中的HAWB2文档
          return job.seaCargoDocs.find(doc => doc.docType === 'HAWB2' && doc.uuid === this.selectedHawb2) || null
        }
      }
      return job
    },
    getFormFieldValueFormatter(fieldName, pageType, job) {
      if (this.isSe) {
        if (pageType === UICustomConstants.SEA_BOOKING_MBL) {
          if (fieldName === 'vesselVoyage') {
            const formData = this.getPageFormData(pageType, fieldName, job)
            if (formData) {
              if (job.barge && formData.printParam6) {
                return (job.bargeVessel || '') + ' ' + (job.bargeVoyage || '')
              } else {
                return (job.vessel || '') + ' ' + (job.voyageFlight || '')
              }
            }
          }
        }
      }
      const value = this.getFormFieldValue(fieldName, pageType, job)
      if (this.isSe) {
        if (pageType === UICustomConstants.SEA_BOOKING_MBL) {
          if (fieldName === 'requestReleaseState') {
            return value === 0 ? this.$t('SeaExport.NotApplying') : this.$t('SeaExport.Requested')
          }
        }
        if (pageType === UICustomConstants.SEA_BOOKING_HBL) {
          if (fieldName === 'requestReleaseState') {
            return value === 0 ? this.$t('SeaExport.NotApplying') : this.$t('SeaExport.Requested')
          }
          if (fieldName === 'longTermGuaranteeState') {
            return SystemConstants.LONG_TERM_GUARANTEE_STATE_MAP.get(value)
          }
          if (fieldName === 'singleTicketGuaranteeState') {
            return SystemConstants.SINGLE_TICKET_GUARANTEE_STATE_MAP.get(value)
          }
        }
      }
      return value
    },
    getFormFieldValue(fieldName, pageType, job) {
      if (this.isSe) {
        if (pageType === UICustomConstants.SEA_BOOKING_BASIC) {
          if (fieldName === 'haulageCompany') {
            if (job.trucking && job.trucking.length > 0) {
              return job.trucking[0].carryingCmp
            }
            return 0
          }
          if (fieldName === 'warehouseCmp') {
            if (job.warehouseInfo && job.warehouseInfo.length > 0) {
              return job.warehouseInfo[0].warehouseCmp
            }
            return 0
          }
          if (fieldName === 'warehouseName') {
            if (job.warehouseInfo && job.warehouseInfo.length > 0) {
              return job.warehouseInfo[0].warehouseName
            }
            return ''
          }
          if (fieldName === 'backUpWarehouse') {
            if (job.backUpWarehouseInfo && job.backUpWarehouseInfo.length > 0) {
              return job.backUpWarehouseInfo[0].warehouseCmp
            }
            return 0
          }
          if (fieldName === 'txtWhs') {
            if (job.warehouseIn && job.warehouseIn.length > 0) {
              return JobUtils.getWarehouseInDetail(job.warehouseIn)
            }
            return ''
          }
        } else if (pageType === UICustomConstants.SEA_BOOKING_MBL) {
          if (fieldName === 'sailingDate') {
            const val = this.config.syncSignDateBl ? job.atd : job.etd
            return val ? moment(val).format('YYYY-MM-DD') : ''
          }
          if (fieldName === 'paymentTermMBL') {
            return job.paymentTermMBL
          }
          if (fieldName === 'freightTerm') {
            return job.freightTerm
          }
        } else if (pageType === UICustomConstants.SEA_BOOKING_HBL) {
          if (fieldName === 'sailingDate') {
            const val = this.config.syncSignDateBl ? job.atd : job.etd
            return val ? moment(val).format('YYYY-MM-DD') : ''
          }
          if (fieldName === 'soNo') {
            return job.soNo ? job.soNo.replace(/\n/g, ',') : ''
          }
        } else if (pageType === UICustomConstants.SEA_BOOKING_VIRTUAL_HBL) {
          if (fieldName === 'etd') {
            return job.etd ? moment(job.etd).format('YYYY-MM-DD') : ''
          }
          if (fieldName === 'freightTermHBL') {
            return job.freightTermHBL
          }
        }
      }
      const formData = this.getPageFormData(pageType, fieldName, job)
      if (this.isSe) {
        if (pageType === UICustomConstants.SEA_BOOKING_MBL) {
          if (fieldName === 'lblCfrState') {
            return ColumnUtils.cfmStatusLabelFunc(formData.cfmState)
          }
          if (fieldName === 'lblReleaseState') {
            return ColumnUtils.releaseStateLabelFunc(formData.releaseState)
          }
          if (fieldName === 'billLadingIssued') {
            return ColumnUtils.bliStatusLabelFunc(formData.bliStatus)
          }
        } else if (pageType === UICustomConstants.SEA_BOOKING_HBL) {
          if (fieldName === 'lblCfrState') {
            return ColumnUtils.cfmStatusLabelFunc(formData.cfmState)
          }
          if (fieldName === 'lblReleaseState') {
            return ColumnUtils.releaseStateLabelFunc(formData.releaseState)
          }
          if (fieldName === 'billLadingIssued') {
            return ColumnUtils.bliStatusLabelFunc(formData.bliStatus)
          }
        } else if (pageType === UICustomConstants.SEA_BOOKING_VIRTUAL_HBL) {
          if (fieldName === 'lblCfrState') {
            return ColumnUtils.cfmStatusLabelFunc(formData.cfmState)
          }
          if (fieldName === 'billLadingIssued') {
            return ColumnUtils.bliStatusLabelFunc(formData.bliStatus)
          }
        }
      }
      fieldName = this.getActualFieldName(pageType, fieldName)
      if (formData) {
        const value = formData[fieldName]
        return typeof value === 'number' ? value : (value || '')
      }
      return ''
    },
    setFormFieldValue(fieldName, pageType, job, value) {
      if (this.isSe) {
        if (pageType === UICustomConstants.SEA_BOOKING_BASIC) {
          if (fieldName === 'haulageCompany') {
            if (job.trucking && job.trucking.length > 0) {
              job.trucking[0].carryingCmp = value
            }
            return
          }
          if (fieldName === 'warehouseCmp') {
            if (job.warehouseInfo && job.warehouseInfo.length > 0) {
              job.warehouseInfo[0].warehouseCmp = value
            }
            return
          }
          if (fieldName === 'warehouseName') {
            if (job.warehouseInfo && job.warehouseInfo.length > 0) {
              job.warehouseInfo[0].warehouseName = value
            }
            return
          }
          if (fieldName === 'backUpWarehouse') {
            if (job.backUpWarehouseInfo && job.backUpWarehouseInfo.length > 0) {
              job.backUpWarehouseInfo[0].warehouseCmp = value
            }
            return
          }
        }
      }
      const formData = this.getPageFormData(pageType, fieldName, job)
      fieldName = this.getActualFieldName(pageType, fieldName)
      if (formData) {
        formData[fieldName] = value
      }
    },
    // 设置job值
    setJobValue(fieldName, value, pageType, targetJob = null) {
      const jobToModify = targetJob || this.job

      // 设置job字段
      const formData = this.getPageFormData(pageType, fieldName, jobToModify)
      const actualFieldName = this.getActualFieldName(pageType, fieldName)

      if (formData) {
        if (targetJob) {
          // 对于外部job，直接设置属性
          formData[actualFieldName] = value
        } else {
          // 对于当前job，使用Vue的响应式设置
          this.$set(formData, actualFieldName, value)
        }
      }

      // 只有在修改当前job时才需要构建AI上下文和同步
      if (!targetJob) {
        // 构建AI上下文
        this.aiContext = this.buildAIContext().then(context => {
          this.aiContext = context
        })

        if (this.debug) {
          return
        }

        // 只在非禁用状态下设置syncJobFull
        if (this.jobFull.jobNo && !this.checkDisabled) {
          const syncFormData = this.getPageFormData(pageType, fieldName, this.syncJobFull)
          if (syncFormData) {
            this.$set(syncFormData, actualFieldName, value)
          }
        }
      }
    },
    // 获取元素组件
    async getElementComponent(element) {
      return getElementComponent(element)
    },
    // 获取元素属性
    getElementProps(element, job) {
      try {
        return getElementProps(element, job)
      } catch (error) {
        console.error(`[元素属性] 获取失败: ${element.elementName}`, error)
        return {
          type: 'text',
          clearable: true
        }
      }
    },
    // 判断是否为选择类型
    isSelectType(component) {
      return isSelectType(component)
    },
    // 获取选项数据
    getElementSelectOptions(element) {
      try {
        return getElementSelectOptions(element)
      } catch (error) {
        console.error(`[选项获取] 获取失败: ${element.elementName}`, error)
        return []
      }
    },
    // 获取选项属性
    getOptionProp(item, index, element, propName) {
      try {
        return getOptionProp(item, index, element, propName)
      } catch (error) {
        console.error(`[选项属性] 获取失败: ${element.elementName}`, error)
        return null
      }
    },
    // 判断是否为只读分块
    isReadOnlyPagePart(pagePart) {
      return isReadOnlyPagePart(pagePart)
    },
    // 判断是否为只读元素
    isReadOnlyElement(element) {
      return isReadOnlyElement(element)
    },
    // 判断元素是否应该被禁用
    isElementDisabled(element) {
      // 批量模式下，HBL字段不依赖文档选择状态（HAWB2页签在批量模式下不可用）
      if (this.isBatchMode) {
        return this.checkDisabled || this.isReadOnlyElement(element)
      }

      if (this.isSe) {
        // HBL、HBL拆单元素：如果没有选中的HBL文档，则禁用
        if (element.pageType === UICustomConstants.SEA_BOOKING_HBL) {
          return !this.selectedHbl
        }

        // 形式分单元素：如果没有选中的HAWB2文档，则禁用
        if (element.pageType === UICustomConstants.SEA_BOOKING_VIRTUAL_HBL) {
          return !this.selectedHawb2
        }
      }

      // 其他情况使用原有的禁用逻辑
      return this.checkDisabled || this.isReadOnlyElement(element)
    },
    getElementOptionsAndLabel(element) {
      const elementName = element.elementName
      const currentValue = this.getFormFieldValue(elementName, element.pageType, this.job)
      const componentInfo = getElementComponentInfo(element)

      let options = []
      let label = ''

      // 包含cargo-advance-select子组件的组件类型
      const cargoSelectComponentTypes = ['cargo-advance-select',
        'cargo-company-select', 'cargo-sales-company-select',
        'cargo-office-select', 'cargo-department-select',
        'cargo-department-new-select', 'cargo-user-select']

      // 包含cargo-advance-select子组件的se-组件类型
      const seSelectComponentTypes = ['se-loading-port-code']

      // 合并所有包含cargo-advance-select的组件类型
      const advanceSelectComponentTypes = [...cargoSelectComponentTypes, ...seSelectComponentTypes]

      // 对于 el-select 组件，使用 elementSelectOptions
      const elementComponentType = componentInfo.elementComponentType
      let elementRef = null
      let tagName = ''
      let tagValue = ''
      elementRef = this.$refs[element.elementName]?.[0]
      if (!elementRef) {
        console.warn('[AI上下文] 元素', element.elementName, '未找到组件引用对象')
        return { options: [], label: '' }
      }

      if (elementComponentType === 'el-select') {
        // elementRef = this.$refs[element.elementName][0].$children[0]
        elementRef = this.findComponetRefByName(this.$refs[element.elementName][0], 'CargoElSelect')
        if (elementRef) {
          options = elementRef.options
          tagName = 'label'
          tagValue = 'value'
        } else {
          console.info(`[AI上下文] 元素 ${elementName} 的 elementRef 为空`)
        }
      } else if (advanceSelectComponentTypes.includes(elementComponentType)) {
        if (elementComponentType === 'cargo-advance-select') {
          // 对于cargo-advance-select，使用 elementRef.options 和 elementRef.getLabel()
          // elementRef = this.$refs[element.elementName][0].$children[0]
          elementRef = this.findComponetRefByName(this.$refs[element.elementName][0], 'CargoAdvanceSelect')
        } else {
          // 对于cargo-xxx-select包装组件，则调用子组件获取options和label
          // elementRef = this.$refs[element.elementName][0].$children[0].$children[0].$children[0]
          elementRef = this.findComponetRefByName(this.$refs[element.elementName][0], 'CargoAdvanceSelect')
        }

        if (elementRef) {
          // 重要逻辑，请勿删除
          options = elementRef.options
          tagName = elementRef.ownTagName || elementRef.tagName
          tagValue = elementRef.ownTagValue || elementRef.tagValue

          // 设置label字段用于AI上下文
          options.forEach(option => {
            option.label = elementRef.getLabel(option)
          })
        } else {
          console.info(`[AI上下文] 元素 ${elementName} 的 elementRef 为空`)
        }
      }

      if (!tagName) {
        console.info(`[AI上下文] 元素 ${elementName} 的 tagName 为空`)
      }
      if (!tagValue) {
        console.info(`[AI上下文] 元素 ${elementName} 的 tagValue 为空`)
      }

      const currentOption = options.find(opt => opt[tagValue || 'value'] === currentValue)
      label = currentOption?.label || ''
      return { options, label }
    },
    // 递归查找指定组件
    findComponetRefByName(el, componentName) {
      if (el.$options.name === componentName) {
        return el
      }
      if (el.$children) {
        for (const child of el.$children) {
          const subEl = this.findComponetRefByName(child, componentName)
          if (subEl) {
            return subEl
          }
        }
      }
      return null
    },
    // 构建AI上下文对象，包含选择组件的标签和选项信息
    // TODO: 这部分还有较大工作量，包括架构优化和横向支持
    async buildAIContext() {
      const context = {
        labels: {},
        options: {}
      }

      for (const pagePart of this.pageParts) {
        for (const element of pagePart.elements) {
          if (this.isSelectComponent(element)) {
            const res = this.getElementOptionsAndLabel(element)
            context.labels[element.elementName] = res.label
            context.options[element.elementName] = res.options
          }
        }
      }
      console.log('[AI上下文] AI上下文信息', context)

      return context
    },
    // 判断元素是否应该显示
    isElementVisible(element) {
      const formData = this.getPageFormData(element.pageType, '', this.job)
      if (!formData) {
        return false
      }

      // 查询该元素的可见性函数
      const visibilityFunctions = functionRegistry.getVisibilityFunctionsByElement(
        this.formUuid,
        element.elementName
      )

      // 如果没有可见性规则，默认显示
      if (visibilityFunctions.length === 0) {
        return true
      }

      // 执行所有可见性规则，如果任何一个返回false，则隐藏元素
      for (const func of visibilityFunctions) {
        const result = functionRegistry.execute(this.formUuid, func.id, this.job, this.aiContext)
        if (!result) {
          return false // 隐藏元素
        }
      }

      return true // 所有规则都通过，显示元素
    },
    // 判断是否为选择组件
    isSelectComponent(element) {
      // 检查元素是否为选择组件
      const componentInfo = getElementComponentInfo(element)
      if (!componentInfo) {
        return false
      }
      const elementComponentType = componentInfo.elementComponentType

      // 1. 包含select的组件（传统选择组件）
      if (elementComponentType.toLowerCase().includes('select')) {
        return true
      }

      // 2. 特定的se-组件（包含选择功能的自定义组件）
      const selectSeComponents = [
        'se-loading-port-code'
        // 未来可以在此处添加其他需要AI上下文支持的se-组件
      ]

      return selectSeComponents.includes(elementComponentType)
    },
    initConfig() {
      this.config.syncSignDateBl = Number(cache.config.get('syncSignDateBl')) === 1
      this.config.seMultiSoMode = Number(cache.config.get('SEMultiSoMode')) === 1
    },
    // 初始化表单数据
    async initializeJobData() {
      if (this.debug) {
        // 调试模式：从formData加载数据
        this.loadFormDataFromDebug()
      } else if (this.isBatchMode) {
        // 批量模式：加载一个空的表单用于输入
        this.loadFormDataForBatch()
      } else {
        // 单任务模式：从API加载数据
        const currentJobUuid = (this.jobs && this.jobs.length > 0) ? this.jobs[0].uuid : this.jobUuid
        if (!currentJobUuid) {
          this.$message.error(this.$t('CargoMsg.Cargo_JobUuidRequired'))
          return
        }
        try {
          await this.loadFormDataFromApi(currentJobUuid)
        } catch (error) {
          this.$message.error(error.message || this.$t('CargoMsg.Cargo_LoadFormDataFailed'))
        }
      }
    },
    // 为批量模式加载空表单
    loadFormDataForBatch() {
      // 1. 创建一个基础的空对象
      let initialJob = null
      if (this.isSe) {
        initialJob = {
          uuid: null,
          seaEQ: [],
          cargoBulkDetail: [],
          surchargeTerms: [],
          poItems: [],
          seaCargoDocs: [],
          charges: [],
          containers: [],
          containerUsages: []
        }
      } else {
        initialJob = {
          uuid: null
        }
      }

      // 2. 遍历所有页面配置，动态添加所有字段的key，并设置初始值为null
      // 这样可以确保所有字段都是响应式的
      if (this.pageParts && Array.isArray(this.pageParts)) {
        this.pageParts.forEach(part => {
          if (part.elements && Array.isArray(part.elements)) {
            part.elements.forEach(element => {
              // 在 initialJob 对象上预定义字段
              if (initialJob[element.elementName] === undefined) {
                initialJob[element.elementName] = null
              }
            })
          }
        })
      }

      if (this.isSe) {
        new SeaExportJob(initialJob).fillDefaultValue()
        // 3. 确保提单等嵌套结构也已初始化
        this.initCargoDocDebugData(initialJob)
      } else if (this.isAe) {
        new AirExportJob(initialJob).fillDefaultValue()
      }

      // 4. 将这个结构完整的对象赋值给this.job，Vue会使其所有属性都成为响应式
      this.job = initialJob
      this.jobOld = cloneDeep(initialJob) // jobOld也需要是完整的结构

      this.eManage.updateJob(this.job)
      console.log('[数据视图] 已为批量模式加载带所有字段的空表单', this.job)
    },
    // 从调试数据加载表单数据
    loadFormDataFromDebug() {
      const mergedData = { ...this.job, ...(this.formData || {}) }
      const deepClonedData = JSON.parse(JSON.stringify(mergedData))
      this.job = deepClonedData

      // 在此处遍历表单配置，预先声明所有可能的字段，避免在setJobValue时出现undefined
      if (this.pageParts && Array.isArray(this.pageParts)) {
        this.pageParts.forEach(part => {
          // 确保分块中有 elements 数组
          if (part.elements && Array.isArray(part.elements)) {
            part.elements.forEach(element => {
              // 检查 this.job 对象中是否已存在该字段的键
              // 如果不存在（值为 undefined），就用 null 初始化它
              if (this.job[element.elementName] === undefined) {
                // 【重要】必须使用 this.$set 来添加新属性，
                // 以确保这个新添加的 null 本身也是响应式的。
                this.$set(this.job, element.elementName, null)
              }
            })
          }
        })
      }
      this.job.lob = this.lob
      if (this.isSe) {
        new SeaExportJob(this.job).fillDefaultValue()
        this.job.seaEQ = this.job.seaEQ || []
        this.job.cargoBulkDetail = this.job.cargoBulkDetail || []
        this.job.surchargeTerms = this.job.surchargeTerms || []
        this.job.poItems = this.job.poItems || []
        this.job.seaCargoDocs = this.job.seaCargoDocs || []
        this.job.seaCargoDoc = this.job.seaCargoDocs
        this.job.charges = this.job.charges || []
        this.job.containers = this.job.containers || []
        this.job.containerUsages = this.job.containerUsages || []
        this.initCargoDocDebugData()
      } else if (this.isAe) {
        new AirExportJob(this.job).fillDefaultValue()
        this.job.airUld = this.job.airUld || []
        this.job.unitLoadDevices = this.job.unitLoadDevices || []
        this.job.unitLoadDeviceUsages = this.job.unitLoadDeviceUsages || []
      }
      this.eManage.updateJob(this.job)
    },
    initCargoDocDebugData(jobInstance) {
      // 如果没有传入job实例，则使用this.job，以兼容旧的调用方式
      const targetJob = jobInstance || this.job

      const mbl = this.findJobSeaCargoDoc(targetJob, 'MBL')
      if (!mbl) {
        targetJob.seaCargoDocs.push(Object.assign(new SeaCargoDoc().fillDefaultValue().getData(), { docType: 'MBL' }))
      }
      const hbl = this.findJobSeaCargoDoc(targetJob, 'HBL')
      if (!hbl) {
        targetJob.seaCargoDocs.push(Object.assign(new SeaCargoDoc().fillDefaultValue().getData(), { docType: 'HBL' }))
      }
      const hbl2 = this.findJobSeaCargoDoc(targetJob, 'HBL2')
      if (!hbl2) {
        targetJob.seaCargoDocs.push(Object.assign(new SeaCargoDoc().fillDefaultValue().getData(), { docType: 'HBL2' }))
      }
      const hawb2 = this.findJobSeaCargoDoc(targetJob, 'HAWB2')
      if (!hawb2) {
        targetJob.seaCargoDocs.push(Object.assign(new SeaCargoDoc().fillDefaultValue().getData(), { docType: 'HAWB2' }))
      }
    },
    // 从API加载表单数据
    async loadFormDataFromApi(currentJobUuid) {
      const loadJobRelatedFunc = this.isAe ? loadAirExportJobRelated : loadSeaExportJobRelated
      try {
        const response = await loadJobRelatedFunc({ job: true, jobUuid: currentJobUuid })
        if (response && response.data && response.data.job) {
          // 设置job的uuid
          this.$set(this.job, 'uuid', currentJobUuid)
          this.jobFull = { ...response.data.job }
          this.jobState = this.jobFull.jobState
          // 从formConfig.businessType中获取业务类型
          const lob = getBusinessType(this.formConfig.businessType)?.lob || 'SE'

          // 检查是否为禁用状态（只读模式或无效状态）
          if (this.checkDisabled) {
            // 只读模式或无效状态：直接使用loadSeaExportJobRelated返回的数据
            console.log('[调试] 只读模式或无效状态，跳过同步API调用')
            this.syncJobFull = { ...response.data.job }
          } else {
            // 编辑模式且有效状态：构建同步API请求，执行一次空请求以便获取syncJobFull对象
            // 在syncJobFull对象初始化完成后，就可以正常调用getFormFieldValue、setJobValue函数了

            // 构建同步API请求
            const syncRequest = {
              channel: 1,
              branchId: this.$store.getters.currentOfficeId,
              lob: lob,
              jobList: [{
                jobNo: this.jobFull.jobNo,
                lob: lob,
                validFields: [
                  {
                    filed: 'userDefinedField1',
                    value: this.jobFull.userDefinedField1,
                    sort: 0
                  }
                ]
              }],
              needReturnJob: true, // 需要返回完整job
              editableJobStates: this.editableJobStates
            }

            // 调用同步API
            const syncResponse = await uiCustomUpdateFields(syncRequest)
            console.log('[调试] 初始化同步API请求', syncRequest)
            console.log('[调试] 初始化同步API响应', syncResponse)
            if (syncResponse && syncResponse.data && syncResponse.data.jobList && syncResponse.data.jobList[0]) {
              const syncJobFull = syncResponse.data.jobList[0]
              if (this.isSe) {
                syncJobFull.poItems = syncJobFull.poItemsList || []
                delete syncJobFull.poItemsList
                this.buildContainerUsages(syncJobFull)
              } else if (this.isAe) {
                syncJobFull.airUld = syncJobFull.airUld || []
              }
              this.syncJobFull = syncJobFull
              console.log('[调试] 初始化同步API结果', this.syncJobFull)
            }

            if (!this.syncJobFull.jobNo) {
              // syncJobFull对象为空，同步API初始化失败，抛出异常
              throw new Error('syncJobFull对象为空，数据加载失败')
            }
          }

          // 为分块和元素加载数据
          // this.loadFormDataForPagePartsAndElements()

          // 完整保存一份job信息
          this.jobOld = cloneDeep(this.syncJobFull)
          if (this.isSe) {
            if (this.syncJobFull.seaEQ && this.syncJobFull.seaEQ.length > 0) {
              this.jobOld.seaEQ = this.syncJobFull.seaEQ.map(eq => ({ ...eq }))
            }
            if (this.syncJobFull.cargoBulkDetail && this.syncJobFull.cargoBulkDetail.length > 0) {
              this.jobOld.cargoBulkDetail = this.syncJobFull.cargoBulkDetail.map(detail => ({ ...detail }))
            }
            if (this.syncJobFull.surchargeTerms && this.syncJobFull.surchargeTerms.length > 0) {
              this.jobOld.surchargeTerms = this.syncJobFull.surchargeTerms.map(surchargeTerm => ({ ...surchargeTerm }))
            }
            if (this.syncJobFull.poItems && this.syncJobFull.poItems.length > 0) {
              this.jobOld.poItems = this.syncJobFull.poItems.map(poItem => ({ ...poItem }))
            }
            if (this.syncJobFull.seaCargoDocs && this.syncJobFull.seaCargoDocs.length > 0) {
              this.jobOld.seaCargoDocs = this.syncJobFull.seaCargoDocs.map(doc => ({ ...doc }))
            }
            if (this.syncJobFull.charges && this.syncJobFull.charges.length > 0) {
              this.jobOld.charges = this.syncJobFull.charges.map(charge => ({ ...charge }))
            }
            this.buildContainerUsages(this.jobOld)
          }

          this.job = cloneDeep(this.jobOld)
          if (this.isSe) {
            this.buildContainerUsages(this.job)
            ContainerUtil.refreshContainerSeaEQ(this.job)
            this.job.seaCargoDoc = this.job.seaCargoDocs
          } else if (this.isAe) {
            this.buildUnitLoadDevices(this.job)
          }
          this.eManage.updateJob(this.job)

          console.log('[调试] 表单初始化完成', {
            job: this.job,
            jobFull: this.jobFull,
            jobOld: this.jobOld,
            syncJobFull: this.syncJobFull
          })
        }
      } catch (error) {
        console.error('[调试] 加载表单数据失败:', error)
        throw error
      }
    },
    buildUnitLoadDevices(job) {
      job.unitLoadDevices = job.unitLoadDevices || []
      const unitLoadDeviceUsages = []
      for (const unitLoadDevice of job.unitLoadDevices) {
        if (unitLoadDevice.unitLoadDeviceUsage) {
          for (const unitLoadDeviceUsage of unitLoadDevice.unitLoadDeviceUsage) {
            unitLoadDeviceUsage.unitLoadDevice = unitLoadDevice
            unitLoadDeviceUsages.push(unitLoadDeviceUsage)
          }
        }
      }
      job.unitLoadDeviceUsages = unitLoadDeviceUsages
    },
    // 初始化特定业务类型的状态信息
    async initializeBusinessStates() {
      // 跳过调试模式和未成功加载数据的情况
      if (this.debug || !this.jobFull.jobNo) {
        return
      }

      console.log('[调试] 初始化业务状态，上下文对象', this.context)
      if (!this.context) {
        console.log('[调试] 上下文对象为空，跳过初始化业务状态')
        return
      }

      // 海运出口相关业务状态
      if (this.formConfig.businessType === BUSINESS_TYPE.SEA_EXPORT.value) {
        // 提单流水号打印验证
        this.originalPrintCheckSerialNo = Number(this.context.config.get('originalPrintCheckSerialNo')) === 1
      }
    },
    // 初始化文档选择
    initializeDocumentSelections() {
      let hasChange = false

      if (this.isSe) {
        // 如果有可用的HBL文档，选择第一个
        if (this.availableHblDocuments.length > 0) {
          this.selectedHbl = this.availableHblDocuments[0].value
          hasChange = true
        }

        // 如果有可用的HAWB2文档，选择第一个
        if (this.availableHawb2Documents.length > 0) {
          this.selectedHawb2 = this.availableHawb2Documents[0].value
          hasChange = true
        }
      }

      if (hasChange) {
        this.$nextTick(() => {
          this.$nextTick(() => {
            this.eManage.updateFormData()
          })
        })
      }

      console.log('[调试] 文档选择初始化完成', {
        selectedHbl: this.selectedHbl,
        selectedHawb2: this.selectedHawb2,
        availableHblDocuments: this.availableHblDocuments,
        availableHawb2Documents: this.availableHawb2Documents
      })
    },
    // 初始化UI自定义配置信息
    async initializeUICustomPageInfo() {
      if (Number(cache.hqConfig.get('UICustomization')) === 1) {
        const loading = getLoading()
        try {
          let routePath = ''
          if (this.formConfig.businessType === BUSINESS_TYPE.SEA_EXPORT.value) {
            routePath = '/SeaExport/BookingNew'
          } else if (this.formConfig.businessType === BUSINESS_TYPE.AIR_EXPORT.value) {
            routePath = '/AirExport/AeBooking'
          }
          await UICustomUtil.loadUICustomPageInfo(routePath, this.$store.getters.currentOfficeId)
        } finally {
          loading.close()
        }
      }
    },
    // 处理元素输入
    handleElementInput(element, value, convertValue) {
      console.log('[调试] handleElementInput 被调用:', {
        元素名: element.elementName,
        组件类型: this.elementComponentMap[element.elementName],
        值: value
      })
      if (convertValue) {
        value = convertValue(value)
      } else {
        value = this.toConvertValueType(element, value)
      }

      if (this.isUpdating) return

      this.isUpdating = true
      this.setJobValue(element.elementName, value, element.pageType)
      this.isUpdating = false
    },
    // 转换值类型
    toConvertValueType(element, value) {
      if (!element) {
        return value
      }
      let model = null
      if (element.pageType === UICustomConstants.AIR_BOOKING_BASIC) {
        model = new AirExportJob().fillDefaultValue().getData()
      }
      if (model) {
        const fieldDefualtVal = model[element.elementName]
        if (typeof fieldDefualtVal === 'number') {
          return value || 0
        }
      }
      return value
    },
    // 处理元素变更
    async handleElementChange(element, value, convertValue) {
      console.log('[调试] handleElementChange 被调用:', {
        元素名: element.elementName,
        组件类型: this.elementComponentMap[element.elementName],
        值: value,
        是否为数字输入: this.isNumberInput(element)
      })
      if (convertValue) {
        value = convertValue(value)
      } else {
        value = this.toConvertValueType(element, value)
      }

      if (this.isUpdating) return
      if (this.isNumberInput(element)) {
        this.debouncedProcessElementChange(element, value)
      } else {
        this.processElementChange(element, value)
      }
    },
    async processElementChange(element, value, fields) {
      this.isUpdating = true
      try {
        // 调试：查看值的结构
        console.log('[调试] 组件回调值:', {
          元素: element.elementName,
          值类型: typeof value,
          值: value,
          是否为对象: value && typeof value === 'object',
          对象属性: value && typeof value === 'object' ? Object.keys(value) : [],
          字段: fields
        })

        // 更新表单数据 - 保持对象结构，因为其他组件可能依赖这个结构
        if (fields) {
          for (const field of fields) {
            this.setJobValue(field.key, field.value, element.pageType)
          }
        } else {
          this.setJobValue(element.elementName, value && typeof value === 'object' ? value.value : value, element.pageType)
        }

        // 1. 首先触发同步API（系统默认同步规则）
        if (!this.debug && !this.checkDisabled && !this.isBatchMode) {
          const syncResult = await this.syncAPI(element.elementName, value && typeof value === 'object' ? value.value : value, element.pageType, fields)
          console.log('[调试] 同步API结果:', syncResult)
        }

        // 2. 然后执行AI同步规则（用户自定义规则，优先级更高）
        const syncFunctions = functionRegistry.getSyncFunctions(this.formUuid)
        // 过滤出由当前元素触发的规则
        const matchedRules = syncFunctions.filter(({ metadata }) => metadata?.trigger?.element === element.elementName)

        console.log('[同步规则] 当前元素触发的规则:', {
          元素: element.elementName,
          匹配规则数: matchedRules.length,
          总规则数: syncFunctions.length
        })

        // 如果有匹配的规则，确保AI上下文已构建完成
        if (matchedRules.length > 0) {
          console.log('[同步规则] 构建AI上下文...')
          this.aiContext = await this.buildAIContext()
          console.log('[同步规则] AI上下文构建完成:', this.aiContext)
        }

        // 执行匹配的同步规则
        for (const { id, metadata } of matchedRules) {
          console.log('[同步规则] 执行规则:', {
            规则ID: id,
            触发元素: metadata.trigger.element
          })

          const result = functionRegistry.execute(this.formUuid, id, this.job, this.aiContext)
          console.log('[同步规则] 执行结果:', result)

          // 处理执行结果
          if (result && result.executed && result.newValue !== undefined && result.targetelement) {
            // 更新目标字段的值
            const targetElement = this.elementMap[result.targetelement]
            this.setJobValue(result.targetelement, result.newValue, targetElement?.pageType)
            console.log('[同步规则] 更新目标字段:', {
              目标字段: result.targetelement,
              新值: result.newValue
            })
          }
        }
        this.eManage.handlePageFieldChange()
      } catch (error) {
        console.error('[同步规则] 执行失败:', error)
        this.$message.error(this.$t('CargoMsg.Cargo_SyncRuleExecutionFailed'))
      } finally {
        this.$nextTick(() => {
          this.isUpdating = false
        })
      }
    },
    // 同步API函数
    async syncAPI(elementName, value, pageType, fields) {
      // 跳过调试模式
      if (this.debug) {
        return false
      }

      // 跳过只读模式或修订模式
      if (this.readonly || this.isRevision) {
        return false
      }

      // 跳过非正操作状态
      if (!this.validJobState) {
        return false
      }

      // 跳过没有jobNo的情况
      if (!this.jobFull.jobNo) {
        return false
      }

      // 跳过禁用状态（包括无效job状态）
      if (this.checkDisabled) {
        console.log('[同步API] 跳过禁用状态，不调用API')
        return false
      }

      // 防止递归调用
      if (this.isSyncing) {
        console.log('[同步API] 跳过递归调用:', elementName)
        return false
      }

      try {
        this.isSyncing = true
        console.log('[同步API] 开始同步字段:', {
          字段名: elementName,
          新值: value,
          字段: fields
        })

        // 记录当前触发的字段名
        this.lastSyncElementName = elementName

        // 从formConfig.businessType中获取业务类型
        const lob = getBusinessType(this.formConfig.businessType)?.lob || 'SE'

        // 构建校验请求
        const modifyFields = []
        if (fields) {
          let sort = 0
          for (const field of fields) {
            // 过滤掉值为 undefined 的字段
            if (field.value !== undefined) {
              modifyFields.push(this.convertModifyField({
                filed: field.key,
                value: field.value,
                sort: sort++
              }))
            }
          }
        } else {
          // 只有当 value 不为 undefined 时才添加
          if (value !== undefined) {
            modifyFields.push(this.convertModifyField({
              filed: elementName,
              value: value,
              sort: 0
            }))
          }
        }

        // 如果所有字段都被过滤（都是 undefined），则跳过API调用
        if (modifyFields.length === 0) {
          console.log('[同步API] 所有字段值均为 undefined，跳过API调用')
          return false
        }

        const validationRequest = {
          channel: 1,
          branchId: this.$store.getters.currentOfficeId,
          lob: lob,
          jobList: [{
            jobNo: this.jobFull.jobNo,
            lob: lob,
            modifyFields
          }],
          editableJobStates: this.editableJobStates
        }

        // 根据业务类型设置jobList，用户编辑可能是多步的，需要把上一步的编辑状态传给接口
        if (this.jobFull.jobNo) {
          const jobListName = this.JOB_LIST_NAMES[lob] || 'seJobList'
          const providedJob = cloneDeep(this.syncJobFull)
          if (this.isSe) {
            providedJob.poItemsList = providedJob.poItems
            delete providedJob.poItems
          }
          cleanCircleObject(providedJob)
          if (this.isSe) {
            if (providedJob.containerUsages) {
              for (const item of providedJob.containerUsages) {
                if (item.container && item.container.containerUsage === '') { // 避免后台fastjson解析报错
                  item.container.containerUsage = null
                }
              }
            }
          }
          validationRequest[jobListName] = [providedJob]
        }

        // 在调用updateFields之前，需要先调用checkUpdateFields进行验证
        const validationResponse = await uiCustomCheckUpdateFields(validationRequest)
        this.validationResult = validationResponse
        this.validationRequest = validationRequest
        this.saveAfterValidate = false
        // 如果没有错误则执行字段同步，否则输出日志并返回
        // 不要频繁弹窗，以免影响用户体验
        if (this.hasError(validationResponse)) {
          console.log('[同步API] 同步API请求验证失败，请求内容', validationRequest)
          console.log('[同步API] 同步API请求验证失败，响应内容', validationResponse)
          return false
        }
        const validFields = validationResponse.data.jobList[0].validFields
        if (validFields.length === 0) {
          return false
        }
        await this.handleValidFieldsConfirm(validFields)

        // 构建同步API请求
        const syncRequest = {
          channel: 1,
          branchId: this.$store.getters.currentOfficeId,
          lob: lob,
          jobList: [{
            jobNo: this.jobFull.jobNo,
            lob: lob,
            validFields
          }],
          needReturnJob: true, // 需要返回完整job
          editableJobStates: this.editableJobStates
        }

        // 根据业务类型设置jobList，用户编辑可能是多步的，需要把上一步的编辑状态传给接口
        if (this.syncJobFull.jobNo) {
          const jobListName = this.JOB_LIST_NAMES[lob] || 'seJobList'
          const providedJob = cloneDeep(this.syncJobFull)
          if (this.isSe) {
            providedJob.poItemsList = providedJob.poItems
            delete providedJob.poItems
          }
          cleanCircleObject(providedJob)
          if (this.isSe) {
            if (providedJob.containerUsages) {
              for (const item of providedJob.containerUsages) {
                if (item.container && item.container.containerUsage === '') { // 避免后台fastjson解析报错
                  item.container.containerUsage = null
                }
              }
            }
          }
          syncRequest[jobListName] = [providedJob]
        }

        // 调用同步API
        const response = await uiCustomUpdateFields(syncRequest)
        console.log('[同步API] 请求数据:', syncRequest)
        console.log('[同步API] 响应数据:', response)

        if (response && response.data && response.data.jobList && response.data.jobList[0]) {
          this.syncJobFull = response.data.jobList[0]
          if (this.isAe) {
            this.syncJobFull.airUld = this.syncJobFull.airUld || []
          }
          this.coverEntity(this.syncJobFull, this.job)

          // // 比较syncJobFull与当前job，找出变化的字段
          // const changedFields = this.compareJobObjects(elementName, pageType)
          // console.log('[同步API] 同步API检测的字段变化:', changedFields)

          // // 应用变化的字段到UI
          // this.applyChangedFields(changedFields, pageType)
        } else {
          // 暂时不在交互层面弹出消息，避免影响用户体验
          console.warn('[同步API] 同步API返回的结果异常', response)
        }

        console.log(`[同步API] 修改值同步完成`, { elementName, value, fields })
      } catch (error) {
        console.error('[同步API] 同步失败:', error)
        // 不显示错误消息，避免影响用户体验
        console.warn(`[同步API] 修改值同步失败:`, { elementName, value, fields }, error)
      } finally {
        this.$nextTick(() => {
          this.isSyncing = false
        })
      }
      return true
    },
    hasError(validationResult) {
      if (!validationResult?.data?.jobList?.[0]) {
        return false
      }
      const job = validationResult.data.jobList[0]
      return job.messageKey && job.messageKey !== '未找到要修改的字段' ||
        job.validFields?.some(field => field.canNotUpdate)
    },
    async handleValidFieldsConfirm(validFields) {
      for (const validField of validFields) {
        await this.handleValidFieldConfirm(validField)
      }
    },
    async handleValidFieldConfirm(validField) {
      if (validField.showChoose && validField.messageKey) {
        const flag = await this.$confirm(this.$t(validField.messageKey, validField.messageValue || []), this.$t('Message.Prompt'), {
          confirmButtonText: this.$t('common.btn.ok'),
          cancelButtonText: this.$t('common.btn.cancel'),
          dangerouslyUseHTMLString: true,
          closeOnClickModal: false,
          type: 'warning'
        }).then(() => true).catch(() => false)
        if (!flag) {
          validField.value = false
          if (validField.hasChild && validField.nChild) {
            await this.handleValidFieldConfirm(validField.nChild)
            return
          }
        }
      }
      if (validField.hasChild && validField.yChild) {
        await this.handleValidFieldsConfirm(validField.yChild)
      }
    },
    // 将newEntity中的数据覆盖到oldEntity中
    coverEntity(newEntity, oldEntity) {
      for (const key in newEntity) {
        const newValue = newEntity[key]
        const oldValue = oldEntity[key]
        // 处理子表（列表）
        if (Array.isArray(newValue) && newValue.length > 0 && newValue[0] instanceof Object && newValue[0].uuid !== undefined) {
          if (Array.isArray(oldValue) && oldValue.length > 0) {
            if (oldValue[0] !== null && typeof oldValue[0] === 'object' && !Array.isArray(oldValue[0])) {
              // 基于 uuid 合并数组
              for (const item of newValue) {
                const oldItem = oldValue.find(oldItem => oldItem.uuid === item.uuid)
                if (oldItem) {
                  this.coverEntity(item, oldItem)
                } else {
                  // oldValue.push(cloneDeep(item))
                }
              }
            } else {
              oldEntity[key] = cloneDeep(newValue)
            }
          } else {
            oldEntity[key] = cloneDeep(newValue)
          }
        }
        // 处理子表（非列表）
        else if (newValue instanceof Object && newValue.uuid !== undefined) {
          if (oldValue !== null && typeof oldValue === 'object' && !Array.isArray(oldValue)) {
            // 递归处理嵌套对象
            this.coverEntity(newValue, oldValue)
          } else {
            oldEntity[key] = cloneDeep(newValue)
          }
        } else {
          if (newValue instanceof Object) {
            oldEntity[key] = cloneDeep(newValue)
          } else {
            oldEntity[key] = newValue
          }
        }
      }
    },
    // 验证单个元素
    validateElement(element) {
      // 跳过验证被禁用的元素
      if (this.isElementDisabled(element)) {
        return
      }

      // 清除当前元素的错误
      this.$delete(this.elementErrors, element.elementName)

      // 如果是必填字段，检查是否为空
      if (element.isRequire || element.isDefaultRequire) {
        if (this.isSe) {
          if (element.elementName === 'seaEq') {
            if (this.job.containerLoadType === 'FCL' || this.job.containerLoadType === 'BB' || this.job.containerLoadType === 'BCF' || this.job.containerLoadType === 'SPA') {
              if (!this.job.seaEQ || this.job.seaEQ.length === 0) {
                this.$set(this.elementErrors, element.elementName, this.$t('CargoMsg.Cargo_ValueRequired'))
                return
              }
              if (this.job.containerLoadType === 'SPA') {
                for (const seaEq of this.job.seaEQ) {
                  if (!seaEq.eqQuantity) {
                    this.$set(this.elementErrors, element.elementName, this.$t('CargoMsg.Cargo_ValueRequired'))
                    return
                  }
                  if (!seaEq.sizeLength || !seaEq.sizeWidth || !seaEq.sizeHeight) {
                    this.$set(this.elementErrors, element.elementName, this.$t('CargoMsg.Cargo_ValueRequired'))
                    return
                  }
                }
              } else {
                for (const seaEq of this.job.seaEQ) {
                  if (!seaEq.eqQuantity) {
                    this.$set(this.elementErrors, element.elementName, this.$t('CargoMsg.Cargo_ValueRequired'))
                    return
                  }
                }
              }
            }
            return
          }
        }

        const value = this.getFormFieldValue(element.elementName, element.pageType, this.job)
        if (value === undefined || value === null || value === '') {
          this.$set(this.elementErrors, element.elementName, this.$t('CargoMsg.Cargo_ValueRequired'))
        }
      }
    },
    // 验证所有元素
    validateAllElements() {
      // 清除之前的错误
      this.elementErrors = {}

      // 遍历所有页面部分和元素进行验证
      for (const pagePart of this.pageParts) {
        for (const element of pagePart.elements) {
          this.validateElement(element)
        }
      }
    },
    // 处理提交
    async handleSubmit() {
      // 构建AI上下文
      this.aiContext = await this.buildAIContext()

      // 提交前验证所有元素
      this.validateAllElements()

      // 检查是否有错误
      if (Object.keys(this.elementErrors).length > 0) {
        this.$message.error(this.$t('CargoMsg.Cargo_FixFormErrors'))
        return
      }

      // 获取所有注册的验证函数
      const validationFunctions = functionRegistry.getValidationFunctions(this.formUuid)
      if (validationFunctions.length > 0) {
        // 执行所有验证函数
        for (const { id, metadata } of validationFunctions) {
          const result = functionRegistry.execute(this.formUuid, id, this.job, this.aiContext)
          console.log('[函数注册表] 规则执行 result', result)
          if (result === false) {
            // 使用metadata中的elements作为错误key
            const ruleElementNames = metadata?.elements
            const errorMessage = metadata?.errorMessage

            // 如果errorMessage是语言列表，则获取本地化名称
            const errorMessageLocalized = Array.isArray(errorMessage) ? getLocalizedName(metadata, 'errorMessage') : errorMessage

            if (ruleElementNames) {
              for (const ruleElementName of ruleElementNames) {
                this.$set(this.elementErrors, ruleElementName, errorMessageLocalized)
              }
            }
          }
        }

        // 再次检查是否有验证错误
        if (Object.keys(this.elementErrors).length > 0) {
          this.$message.error(this.$t('CargoMsg.Cargo_FixFormErrors'))
          return
        }
      }

      if (this.debug) {
        // 调试模式：保存表单数据
        this.$emit('update:formData', this.job)
        this.$message.success(this.$t('CargoMsg.Cargo_Saved'))
        this.$emit('close')
      } else if (this.isBatchMode) {
        // 批量模式：检查是否需要确认提示，然后调用批量保存逻辑
        await this.handleBatchSubmitWithConfirm()
      } else if (this.checkDisabled) {
        // 只读模式或无效状态：直接显示成功消息并关闭
        console.log('[调试] 只读模式或无效状态，跳过API调用')
        this.$message.success(this.$t('CargoMsg.Cargo_SaveSucceed'))
        this.$emit('close')
      } else {
        // 单任务模式：调用API保存
        await this.handleSingleSubmit()
      }
    },
    async handleSingleSubmit() {
      try {
        // 从formConfig.businessType中获取业务类型
        const lob = getBusinessType(this.formConfig.businessType)?.lob || 'SE'

        // 这里需要对比this.jobOld和this.job的值变化，并创建修改请求
        console.log('[调试] job对象变化对比', {
          初始值: this.jobOld,
          当前值: this.job
        })
        this.needMergeDataBeforeSave = {}
        const modifyFields = []
        let index = 0
        // 需要比较job表和子表的字段变化
        const compareKeys = [...this.COMPARE_JOB_FIELDS]
        let jobObjs = [this.job]
        let jobOldObjs = [this.jobOld]
        const compareKeyNames = ['job']
        for (const compareKey of compareKeys) {
          if (this.job[compareKey] === undefined) {
            continue
          }
          // 将job和子表对象合并成一个数组
          jobObjs = [...jobObjs, ...this.job[compareKey]]
          jobOldObjs = [...jobOldObjs, ...this.jobOld[compareKey]]
          compareKeyNames.push(...Array(this.job[compareKey].length).fill(compareKey))
        }
        for (let i = 0; i < jobObjs.length; i++) {
          const jobObj = jobObjs[i]
          const jobOldObj = jobOldObjs[i]
          for (const key in jobObj) {
            if (i === 0 && this.SKIP_JOB_FIELDS.includes(key)) {
              continue
            }
            if (jobObj[key] !== jobOldObj[key] && jobOldObj[key] !== undefined) {
              console.log('[调试] 检测到字段变化:', {
                比较对象: compareKeyNames[i],
                字段名: key,
                原值: jobOldObj[key],
                新值: jobObj[key]
              })
              modifyFields.push({
                filed: key,
                value: jobObj[key],
                // TODO: 这个修改顺序其实是重要的，但我们在自定义表单的提交是同时的，因此无法得到逻辑顺序
                sort: index++
              })
              const linkItems = this.getLinkCheckItems(key)
              if (linkItems) {
                for (const linkItem of linkItems) {
                  modifyFields.push({
                    filed: linkItem.field,
                    value: linkItem.value,
                    sort: index++
                  })
                }
              }
            }
          }
        }

        if (this.isSe) {
          // 检查报关信息数据变化
          this.isCustomsInfoChanges = this.syncCustomsInfo()

          // 检查费用数据变化
          this.isChargeChanges = await this.syncCharges()

          // 检查箱型箱量变化
          this.isSeaEqChanges = await this.syncSeaEq()

          // 检查关单信息变化
          this.isPoItemsChanges = this.syncPoItems()

          // 检查附加费条款变化
          this.isSurchargeTermsChanges = this.syncSurchargeTerms()

          // 检查HBL费用变化
          this.isHblFreightChargesChanges = this.syncHblFreightCharges()

          // 检查装箱信息变化
          this.isContainerUsageChanges = await this.syncContainerUsage()
        } else if (this.isAe) {
          // 检查集装器变化
          this.isAirUldChanges = this.syncAirUld()
          this.isUnitLoadDevicesChanges = this.syncUnitLoadDevices()
        }

        // 如果修改字段为空，则不进行校验
        if (this.isSe) {
          if (modifyFields.length === 0 && !this.isCustomsInfoChanges &&
            !this.isChargeChanges && !this.isPoItemsChanges && !this.isSurchargeTermsChanges && !this.isHblFreightChargesChanges && !this.isContainerUsageChanges) {
            this.$message.success(this.$t('CargoMsg.Cargo_SaveSucceed'))
            this.$emit('close')
            return
          }
        } else {
          if (modifyFields.length === 0 && !this.isAirUldChanges && !this.isUnitLoadDevicesChanges) {
            this.$message.success(this.$t('CargoMsg.Cargo_SaveSucceed'))
            this.$emit('close')
            return
          }
        }

        if (modifyFields.length === 0) {
          // 如果字段修改无变化，但是其他子表数据有变化，则填入一个假修改
          modifyFields.push(
            {
              filed: 'userDefinedField1',
              value: this.jobFull.userDefinedField1,
              sort: 0
            }
          )
        }
        // 构建验证请求数据
        const validationRequest = {
          channel: 1,
          branchId: this.$store.getters.currentOfficeId,
          jobList: [{
            jobNo: this.jobFull.jobNo,
            lob: lob,
            modifyFields: modifyFields
              .map(modifyField => this.convertModifyField(modifyField))
              // 过滤掉值为 undefined 的字段
              .filter(modifyField => modifyField.value !== undefined)
          }],
          editableJobStates: this.editableJobStates
        }

        try {
          // 显示加载状态
          this.loading = getLoading4Cxt()
          // 调用验证API
          const validationResponse = await uiCustomCheckUpdateOrder(validationRequest)
          this.validationResult = validationResponse
          // 存储请求数据供保存API使用
          this.validationRequest = validationRequest
          this.saveAfterValidate = true
          this.showValidationDialog = true
        } finally {
          // 关闭加载状态
          this.loading.close()
        }
      } catch (error) {
        // 排除费用区域，提示信息为空的（来源于弹窗选“否”，无需处理）
        if (!(error instanceof TabError && error.tabName === 'tabCharges' && !error.message)) {
          this.$message.error(error.message || this.$t('CargoMsg.Cargo_SaveFailed'))
        }
      }
    },
    async handleBatchSubmitWithConfirm() {
      try {
        // 检查是否需要显示批量修改确认提示
        const needConfirm = this.checkBatchModificationPrompt()

        if (needConfirm) {
          console.log('[批量提交] 需要显示确认弹窗')
          // 显示确认弹窗
          await this.$confirm(
            this.$t('SystemMaintenance.ConfirmBatchModification'),
            this.$t('SystemMaintenance.BatchModificationConfirm'),
            {
              confirmButtonText: this.$t('common.btn.confirm'),
              cancelButtonText: this.$t('common.btn.cancel'),
              type: 'warning'
            }
          )
          console.log('[批量提交] 用户确认批量修改')
        } else {
          console.log('[批量提交] 无需确认，直接执行批量提交')
        }

        // 确认后或无需确认时执行批量提交
        await this.handleBatchSubmit()
      } catch (error) {
        if (error === 'cancel') {
          console.log('[批量提交] 用户取消批量修改操作')
          return
        }
        // 其他错误继续抛出
        console.error('[批量提交] 批量提交过程中发生错误:', error)
        throw error
      }
    },
    loadBatchModificationPromptConfig() {
      try {
        console.log('[批量修改提示] 开始加载配置, formUuid:', this.formConfig.uuid)
        console.log('[批量修改提示] 租户指令数据:', this.tenantInstructions)

        if (this.tenantInstructions && this.tenantInstructions.length > 0) {
          // 遍历所有租户指令，找到启用批量修改提示的配置
          for (const instruction of this.tenantInstructions) {
            if (instruction.instructionRuleJson &&
                instruction.instructionRuleJson.batchFormUuid === this.formConfig.uuid &&
                instruction.instructionRuleJson.batchModificationPrompt === true) {
              console.log('[批量修改提示] 找到启用批量修改提示的指令:', instruction.chineseName)
              this.batchModificationPromptConfig = true
              return
            }
          }

          console.log('[批量修改提示] 找到指令配置但未启用批量修改提示')
          this.batchModificationPromptConfig = false
        } else {
          console.log('[批量修改提示] 未找到租户指令数据')
          this.batchModificationPromptConfig = false
        }
      } catch (error) {
        console.error('[批量修改提示] 加载配置失败:', error)
        this.batchModificationPromptConfig = false
      }
    },
    checkBatchModificationPrompt() {
      // 直接返回已加载的配置，无需异步操作
      if (this.batchModificationPromptConfig === null) {
        console.log('[批量修改提示] 配置尚未加载完成，默认不显示提示')
        return false
      }

      console.log('[批量修改提示] 使用已加载的配置:', this.batchModificationPromptConfig)
      return this.batchModificationPromptConfig === true
    },
    async handleBatchSubmit() {
      try {
        const lob = getBusinessType(this.formConfig.businessType)?.lob || 'SE'

        // 批量模式下，对比 this.job (用户输入) 和 this.jobOld (初始空对象) 来找出修改
        const modifyFields = []
        let index = 0

        // 1. 检查基本字段的修改
        // 先收集所有被清除的多字段组件的子字段，避免重复处理
        const clearedSubFields = new Set()
        // 记录第一步中已经处理过的主表字段，避免在第二步中重复处理
        const processedMainFields = new Set()

        for (const fieldName in this.clearStates) {
          if (this.clearStates[fieldName] === true && this.isMultiFieldComponent(fieldName)) {
            const subFields = this.getMultiFieldSubFields(fieldName)
            subFields.forEach(subField => clearedSubFields.add(subField))
          }
        }

        for (const key in this.job) {
          // 跳过不需要比较的字段
          if (this.SKIP_JOB_FIELDS.includes(key) || !Object.prototype.hasOwnProperty.call(this.job, key)) {
            continue
          }

          // 跳过已经被多字段组件清除处理的子字段
          if (clearedSubFields.has(key)) {
            continue
          }

          // 如果值不相等，且不是初始的空数组等情况，则认为是用户修改过的字段
          const oldValue = this.jobOld[key]
          const newValue = this.job[key]
          const isOldValueEmpty = oldValue === null || oldValue === undefined || (Array.isArray(oldValue) && oldValue.length === 0)
          const isNewValueEmpty = newValue === null || newValue === undefined || newValue === '' || (Array.isArray(newValue) && newValue.length === 0)

          // 检查是否为清除的字段，或新值非空且与旧值不同时，才视为修改
          const isCleared = this.clearStates[key] === true
          if (isCleared || (!isNewValueEmpty && newValue !== oldValue)) {
            // 记录该字段已经在第一步中处理过了
            processedMainFields.add(key)

            // 处理多字段组件的清除
            if (isCleared && this.isMultiFieldComponent(key)) {
              const subFields = this.getMultiFieldSubFields(key)
              subFields.forEach(subField => {
                processedMainFields.add(subField) // 也记录子字段
                modifyFields.push({
                  filed: subField,
                  value: null, // 多字段组件的子字段都是数值类型，清除时设为null
                  sort: index++
                })
              })
            } else {
              // 处理普通字段
              const clearValue = (this.isDateField(key) || this.isNumberField(key)) ? null : ''
              const finalValue = isCleared ? clearValue : newValue // 被清除的字段根据类型提交不同的值
              modifyFields.push({
                filed: key,
                value: finalValue,
                sort: index++
              })
            }
          }
        }

        // 2. 检查子表字段修改（包括收发通信息）
        const compareKeys = [...this.COMPARE_JOB_FIELDS]
        let jobObjs = [this.job]
        let jobOldObjs = [this.jobOld]
        const compareKeyNames = ['job']
        for (const compareKey of compareKeys) {
          if (this.job[compareKey] === undefined) {
            continue
          }
          // 将job和子表对象合并成一个数组
          jobObjs = [...jobObjs, ...this.job[compareKey]]
          jobOldObjs = [...jobOldObjs, ...this.jobOld[compareKey]]
          compareKeyNames.push(...Array(this.job[compareKey].length).fill(compareKey))
        }

        // 遍历所有对象（主表+子表）进行字段比较
        for (let i = 0; i < jobObjs.length; i++) {
          const jobObj = jobObjs[i]
          const jobOldObj = jobOldObjs[i] || {}

          for (const key in jobObj) {
            // 跳过主表的系统字段
            if (i === 0 && this.SKIP_JOB_FIELDS.includes(key)) {
              continue
            }

            // 跳过已经被多字段组件清除处理的子字段
            if (i === 0 && clearedSubFields.has(key)) {
              continue
            }

            // 跳过第一步中已经处理过的主表字段，避免重复
            if (i === 0 && processedMainFields.has(key)) {
              continue
            }

            // 检查字段值是否发生变化
            if (jobObj[key] !== jobOldObj[key]) {
              // 对于主表字段(i === 0)，只有当jobOldObj[key]不为undefined时才处理
              // 这样可以避免与第一步重复处理初始为undefined的字段
              if (i === 0 && jobOldObj[key] === undefined) {
                continue
              }

              // 对于子表字段，需要使用convertModifyField进行转换
              const rawModifyField = {
                filed: key,
                value: jobObj[key],
                sort: index++
              }

              // 批量模式下也需要转换字段格式，特别是收发通信息字段
              const convertedField = this.convertModifyField(rawModifyField)
              modifyFields.push(convertedField)
            }
          }
        }

        // 批量模式下，暂不支持其他复杂的子组件（如费用、报关）的批量修改
        // 如果没有修改任何字段，则提示并返回
        if (modifyFields.length === 0) {
          this.$message.info(this.$t('CargoMsg.Cargo_NoChangesToSubmit'))
          return
        }

        // 去重：确保相同字段名的情况下，新值覆盖旧值（保留sort值最大的）
        const deduplicatedModifyFields = []
        const fieldMap = new Map()

        // 先按sort排序，确保后处理的字段会覆盖先处理的字段
        modifyFields.sort((a, b) => a.sort - b.sort)

        for (const field of modifyFields) {
          if (field.value !== undefined) {
            // 使用字段名作为key，新值覆盖旧值
            fieldMap.set(field.filed, field)
          }
        }

        // 重新生成sort序号并构建最终的字段数组
        let sortIndex = 0
        for (const field of fieldMap.values()) {
          deduplicatedModifyFields.push({
            ...field,
            sort: sortIndex++
          })
        }

        // 构建批量验证请求
        const validationRequest = {
          channel: 1,
          branchId: this.$store.getters.currentOfficeId,
          jobList: this.jobs.map(job => ({
            jobNo: job.jobNo,
            lob: lob,
            modifyFields: deduplicatedModifyFields
          })),
          editableJobStates: this.editableJobStates
        }

        this.loading = getLoading4Cxt()
        const validationResponse = await uiCustomCheckUpdateOrder(validationRequest)
        this.validationResult = validationResponse
        this.validationRequest = validationRequest
        this.saveAfterValidate = true

        // 检查是否所有任务的 canNotUpdate 都为 false
        let allCanUpdate = true
        if (validationResponse?.data?.jobList) {
          for (const job of validationResponse.data.jobList) {
            if (job.validFields) {
              for (const field of job.validFields) {
                if (field.canNotUpdate === true) {
                  allCanUpdate = false
                  break
                }
              }
            }
            if (!allCanUpdate) {
              break
            }
          }
        }

        // 无论验证通过还是失败，都显示验证对话框
        // 如果所有 canNotUpdate 都为 false，表单验证通过，对话框会显示成功信息
        // 如果存在 canNotUpdate 为 true，表单验证失败，对话框会显示失败原因
        this.showValidationDialog = true
      } catch (error) {
        this.$message.error(error.message || this.$t('CargoMsg.Cargo_BatchSaveFailed'))
      } finally {
        if (this.loading) {
          this.loading.close()
        }
      }
    },
    getLinkCheckItems(sourceField) {
      const sourceElement = this.elementMap[sourceField]
      if (!sourceElement || !sourceElement.pageType) {
        return null
      }
      const e = this.eManage.get(sourceElement.pageType, sourceField)
      if (!e || !e.linkCheckItems) {
        return null
      }
      const result = []
      for (const item of e.linkCheckItems) {
        result.push({
          field: item.elementName,
          value: this.getFormFieldValue(item.elementName, item.pageType, this.job)
        })
      }
      return result
    },
    // 处理取消
    handleCancel() {
      this.elementErrors = {}
      this.$emit('close')
    },

    syncCustomsInfo() {
      if (!this.$refs.tabCustomsInfo?.[0]) {
        return false
      }

      const changedFields = []
      const tabCustomsInfo = this.$refs.tabCustomsInfo[0]
      const result = tabCustomsInfo.getSubmitData()
      for (const key of this.CUSTOMS_INFO_JOB_FIELDS) {
        if (result.job[key] !== this.jobOld[key]) {
          changedFields.push({
            key: key,
            oldValue: this.jobOld[key],
            newValue: result.job[key]
          })
          this.setJobValue(key, result.job[key], UICustomConstants.SEA_BOOKING_BASIC)
        }
      }
      console.log('[调试] 获取并合并报关信息数据，变更数据', changedFields)

      return changedFields.length > 0
    },
    async syncContainerUsage() {
      if (!this.$refs.seContainer?.[0]) {
        return false
      }
      const seContainer = this.$refs.seContainer[0]
      const tabData = await seContainer.getSubmitData()
      delete tabData.job
      if (tabData.seaCargoDoc) {
        tabData.seaCargoDocs = tabData.seaCargoDoc
        delete tabData.seaCargoDoc
      }
      this.mergeSaveDataByUuid(this.needMergeDataBeforeSave, tabData)
      return Object.keys(tabData).length > 0
    },
    mergeSaveDataByUuid(target, source) {
      for (const key in source) {
        const newValue = source[key]
        const oldValue = target[key]
        // 处理数组
        if (Array.isArray(newValue)) {
          if (Array.isArray(oldValue)) {
            for (const item of newValue) {
              const oldItemIndex = oldValue.findIndex(oldItem => oldItem === item || (oldItem.uuid && oldItem.uuid === item.uuid))
              if (oldItemIndex !== -1) {
                oldValue[oldItemIndex] = item
              } else {
                oldValue.push(item)
              }
            }
          } else {
            target[key] = newValue
          }
        }
        // 非数组，直接赋值
        else {
          target[key] = newValue
        }
      }
    },
    buildContainerUsages(job) {
      job.containers = job.containers || []
      job.containerUsages = job.containerUsages || []
      if (job.masterFlag) {
        const slaveMap = new Map()
        if (job.lclSlaves) {
          for (const slave of job.lclSlaves) {
            slaveMap.set(slave.uuid, slave)
          }
        }
        for (const container of job.containers) {
          container.containerUsage = container.containerUsage || []
          for (const usage of container.containerUsage) {
            const slave = slaveMap.get(usage.jobUuid)
            if (!slave) {
              continue
            }
            // 工作号上的相关信息
            usage.jobNo = slave.jobNo
            usage.warehouseNo = slave.warehouseNo
            usage.mbl = slave.mbl
            usage.customerId = slave.customerId
            usage.soNo = slave.soNo
            usage.dischargingPortCode = slave.dischargingPortCode
            usage.destinationPortCode = slave.destinationPortCode
            usage.etd = slave.etd
            usage.operationTips = slave.operationTips
            usage.destinationAgent = slave.destinationAgent
            usage.designationAgent = slave.designationAgent
            usage.userDefinedField1 = slave.userDefinedField1
            usage.userDefinedField2 = slave.userDefinedField2
            usage.userDefinedField3 = slave.userDefinedField3
            usage.userDefinedField4 = slave.userDefinedField4
            usage.jobRemarks = slave.remarks
            usage.warehouseRemarks = slave.warehouseRemarks
            usage.mark = slave.mark
            usage.cargoName = slave.cargoEnglishName
            // 箱子信息
            usage.containerAscription = container.containerAscription
            usage.boxAscription = container.boxAscription
            usage.boxOwner = container.boxOwner
            usage.returnBoxTime = container.returnBoxTime
            usage.eqSize = container.eqSize
            usage.eqType = container.eqType
            usage.containerNo = container.containerNo
            usage.remark = container.remark
            usage.sealNo = container.sealNo
            usage.wharfStowage = container.wharfStowage
            usage.wharfReleaseDate = container.wharfReleaseDate
            usage.customReleaseDate = container.customReleaseDate
            usage.destinationReturnTime = container.destinationReturnTime
            usage.approachDate = container.approachDate
            usage.playDate = container.playDate
            usage.plateApproachDate = container.plateApproachDate
            usage.platePlayDate = container.platePlayDate
            usage.containerWeight = container.containerWeight
            usage.containerId = container.containerId
          }
        }
      } else {
        ContainerUtil.buildContainerUsages(job)
      }
    },
    async syncCharges() {
      if (!this.$refs.tabChargesReceivable?.[0] && !this.$refs.tabChargesPayable?.[0]) {
        return false
      }

      let chargesReceivable = []
      let delChargeUuidsReceivable = []
      if (this.$refs.tabChargesReceivable?.[0]) {
        const tabChargesReceivable = this.$refs.tabChargesReceivable[0]
        const result = await tabChargesReceivable.getSubmitData()
        chargesReceivable = result.charges
        delChargeUuidsReceivable = result.delChargeUuids
      }
      console.log('[调试] 获取并合并费用数据，应收费用', chargesReceivable)
      console.log('[调试] 获取并合并费用数据，删除应收费用', delChargeUuidsReceivable)

      let chargesPayable = []
      let delChargeUuidsPayable = []
      if (this.$refs.tabChargesPayable?.[0]) {
        const tabChargesPayable = this.$refs.tabChargesPayable[0]
        const result = await tabChargesPayable.getSubmitData()
        chargesPayable = result.charges
        delChargeUuidsPayable = result.delChargeUuids
      }
      console.log('[调试] 获取并合并费用数据，应付费用', chargesPayable)
      console.log('[调试] 获取并合并费用数据，删除应付费用', delChargeUuidsPayable)

      const charges = !this.job.charges ? [] : this.job.charges.filter(charge => {
        if (delChargeUuidsReceivable.includes(charge.uuid) ||
            delChargeUuidsPayable.includes(charge.uuid) ||
            charge.uuid.startsWith('addTabCost__') ||
            charge.uuid.startsWith('addTabSelling__')) {
          return false
        }
        return true
      })
      for (const charge of [...chargesReceivable, ...chargesPayable]) {
        if (!charge.uuid) {
          charges.push(charge)
        } else {
          const index = charges.findIndex(c => c.uuid === charge.uuid)
          if (index !== -1) {
            charges[index] = charge
          }
        }
      }

      this.job.charges = charges

      // 只在非禁用状态下设置syncJobFull
      if (!this.checkDisabled) {
        this.syncJobFull.charges = cloneDeep(charges)
        console.log('[调试] 获取并合并费用数据，合并结果', this.job.charges)

        const delChargeUuids = Array.from(new Set([...delChargeUuidsReceivable, ...delChargeUuidsPayable]))
        if (delChargeUuids.length > 0) {
          this.job.delChargeUuids = delChargeUuids
          this.syncJobFull.delChargeUuids = cloneDeep(delChargeUuids)
          console.log('[调试] 获取并合并费用数据，删除费用UUID', this.job.delChargeUuids)
        }
      }

      return chargesReceivable.length > 0 || chargesPayable.length > 0 ||
        delChargeUuidsReceivable.length > 0 || delChargeUuidsPayable.length > 0
    },
    syncSeaEq() {
      const oldSeaEq = this.jobOld.seaEQ
      const newSeaEq = this.job.seaEQ
      if (!oldSeaEq || !newSeaEq) {
        return false
      }
      const oldSeaEqMap = new Map()
      for (const item of oldSeaEq) {
        oldSeaEqMap.set(item.uuid, item)
      }
      let hasChange = false
      for (const item of newSeaEq) {
        if (!item.uuid || !oldSeaEqMap.has(item.uuid)) {
          hasChange = true
          break
        }
        const oldItem = oldSeaEqMap.get(item.uuid)
        for (const key in item) {
          if (item[key] !== oldItem[key]) {
            hasChange = true
            break
          }
        }
        if (hasChange) {
          break
        }
      }
      if (hasChange) {
        this.syncJobFull.seaEQ = cloneDeep(this.job.seaEQ)
      }
      return hasChange
    },
    syncPoItems() {
      const e = this.eManage.get(UICustomConstants.SEA_BOOKING_BASIC, 'customsDeclaration')
      if (e && !e.hide) {
        this.syncJobFull.delPoItemsUuids = this.job.delPoItemsUuids
        return this.syncJobFull.delPoItemsUuids && this.syncJobFull.delPoItemsUuids.length > 0
      } else {
        delete this.syncJobFull.delPoItemsUuids
        return false
      }
    },
    syncSurchargeTerms() {
      console.log('[调试] 检查附加费条款变化', cloneDeep(this.jobOld.surchargeTerms), cloneDeep(this.job.surchargeTerms))
      const oldSurchargeTerms = this.jobOld.surchargeTerms
      const newSurchargeTerms = this.job.surchargeTerms
      if (!oldSurchargeTerms && !newSurchargeTerms) {
        return false
      }
      if ((oldSurchargeTerms && !newSurchargeTerms) || (!oldSurchargeTerms && newSurchargeTerms)) {
        this.syncJobFull.surchargeTerms = cloneDeep(this.job.surchargeTerms || [])
        return true
      }
      if (oldSurchargeTerms.length !== newSurchargeTerms.length) {
        this.syncJobFull.surchargeTerms = cloneDeep(this.job.surchargeTerms)
        return true
      }
      for (let i = 0; i < oldSurchargeTerms.length; i++) {
        if (oldSurchargeTerms[i].chargeItemId !== newSurchargeTerms[i].chargeItemId ||
            oldSurchargeTerms[i].terms !== newSurchargeTerms[i].terms) {
          this.syncJobFull.surchargeTerms = cloneDeep(this.job.surchargeTerms)
          return true
        }
      }
      return false
    },
    syncHblFreightCharges() {
      const hbl = this.findSeaCargoDoc(this.job, 'HBL')
      const syncHbl = this.findSeaCargoDoc(this.syncJobFull, 'HBL')
      if (!hbl || !syncHbl) {
        return false
      }
      if (hbl.freightCharges) {
        syncHbl.freightCharges = cloneDeep(hbl.freightCharges)
        return true
      }
      return false
    },
    syncAirUld() {
      const oldAirUld = this.jobOld.airUld
      const newAirUld = this.job.airUld
      if (!oldAirUld || !newAirUld) {
        return false
      }
      const oldAirUldMap = new Map()
      for (const item of oldAirUld) {
        oldAirUldMap.set(item.uuid, item)
      }
      let hasChange = false
      for (const item of newAirUld) {
        if (!item.uuid || !oldAirUldMap.has(item.uuid)) {
          hasChange = true
          break
        }
        const oldItem = oldAirUldMap.get(item.uuid)
        for (const key in item) {
          if (item[key] !== oldItem[key]) {
            hasChange = true
            break
          }
        }
        if (hasChange) {
          break
        }
      }
      if (hasChange) {
        this.syncJobFull.airUld = cloneDeep(this.job.airUld)
      }
      return hasChange
    },
    syncUnitLoadDevices() {
      if (!this.jobOld.unitLoadDevices || !this.job.unitLoadDevices) {
        return false
      }
      const oldUsageMap = new Map()
      for (const item of this.jobOld.unitLoadDevices) {
        if (item.unitLoadDeviceUsage) {
          for (const unitLoadDeviceUsage of item.unitLoadDeviceUsage) {
            oldUsageMap.set(unitLoadDeviceUsage.uuid, unitLoadDeviceUsage)
          }
        }
      }
      let hasChange = false
      for (const item of this.job.unitLoadDevices) {
        if (item.unitLoadDeviceUsage) {
          for (const unitLoadDeviceUsage of item.unitLoadDeviceUsage) {
            if (!unitLoadDeviceUsage.uuid || !oldUsageMap.has(unitLoadDeviceUsage.uuid)) {
              hasChange = true
              break
            }
            const oldUsage = oldUsageMap.get(unitLoadDeviceUsage.uuid)
            for (const key in oldUsage) {
              if (unitLoadDeviceUsage[key] !== oldUsage[key]) {
                hasChange = true
                break
              }
            }
            if (hasChange) {
              break
            }
          }
        }
        if (hasChange) {
          break
        }
      }
      if (hasChange) {
        const unitLoadDevices = []
        for (const item of this.job.unitLoadDevices) {
          const newItem = { ...item }
          if (item.unitLoadDeviceUsage) {
            newItem.unitLoadDeviceUsage = []
            for (const unitLoadDeviceUsage of item.unitLoadDeviceUsage) {
              const newUsage = { ...unitLoadDeviceUsage }
              delete newUsage.unitLoadDevice
              newItem.unitLoadDeviceUsage.push(newUsage)
            }
          }
          unitLoadDevices.push(newItem)
        }
        this.syncJobFull.unitLoadDevices = unitLoadDevices
      }
      return hasChange
    },
    async saveJob(validFields) {
      if (this.isBatchMode) {
        await this.saveBatchJobs(validFields)
        return
      }

      // 验证通过，调用保存API
      try {
        this.loading = getLoading4Cxt()

        // 从formConfig.businessType中获取业务类型
        const lob = getBusinessType(this.formConfig.businessType)?.lob || 'SE'

        const saveRequest = {
          channel: 1,
          branchId: this.$store.getters.currentOfficeId,
          lob: lob,
          location: LOG_BOOKING_CUSTOM_FORM.code,
          locationParams: 'json:' + JSON.stringify({ customFormUuid: this.formConfig.uuid }),
          editableJobStates: this.editableJobStates
        }

        // 根据业务类型设置jobList，用户编辑可能是多步的，需要把上一步的编辑状态传给接口
        if (this.syncJobFull.jobNo) {
          const jobListName = this.JOB_LIST_NAMES[lob] || 'seJobList'
          const providedJob = cloneDeep(this.syncJobFull)
          for (const key in this.needMergeDataBeforeSave) {
            if (key === 'seaEQ') {
              continue
            }
            providedJob[key] = cloneDeep(this.needMergeDataBeforeSave[key])
          }
          if (this.isSe) {
            providedJob.poItemsList = providedJob.poItems
            delete providedJob.poItems
            if (providedJob.containerUsages) {
              for (const item of providedJob.containerUsages) {
                if (item.container && item.container.containerUsage === '') { // 避免后台fastjson解析报错
                  item.container.containerUsage = null
                }
              }
            }
            delete providedJob.containerUsages
          }
          cleanCircleObject(providedJob)
          if (validFields) {
            for (const validField of validFields) {
              if (validField.canNotUpdate) {
                const element = this.elementMap[validField.filed]
                if (!element || !element.pageType) {
                  continue
                }
                const pageType = element.pageType
                const oldValue = this.getFormFieldValue(validField.filed, pageType, this.jobOld)
                this.setFormFieldValue(validField.filed, pageType, providedJob, oldValue)
              }
            }
          }
          saveRequest[jobListName] = [providedJob]
        } else {
          // syncJobFull如果为空，则表示没有触发过同步API，按照无修改处理
          return
        }

        const saveResponse = await uiCustomSave(saveRequest)

        if (saveResponse.ok && saveResponse.data.successJobList?.includes(this.jobFull.jobNo)) {
          this.$message.success(this.$t('CargoMsg.Cargo_SaveSucceed'))
          this.$emit('saveSuccess')
          this.$emit('close')
        } else {
          this.$message.error(saveResponse.data.msg || this.$t('CargoMsg.Cargo_SaveFailed'))
        }
      } catch (error) {
        console.error('保存失败:', error)
        this.$message.error(this.$t('CargoMsg.Cargo_SaveFailed'))
      } finally {
        this.loading.close()

        this.validationRequest = null
        this.validationResult = null
        this.saveAfterValidate = false
        this.isChargeChanges = false
      }
    },
    // 将用户的修改应用到完整的job数据中
    applyUserModificationsToJob(targetJob, validFields) {
      // 先收集所有被多字段组件清空处理的子字段，避免重复处理
      const processedSubFields = new Set()
      for (const fieldName in this.clearStates) {
        if (this.clearStates[fieldName] === true && this.isMultiFieldComponent(fieldName)) {
          const subFields = this.getMultiFieldSubFields(fieldName)
          subFields.forEach(subField => processedSubFields.add(subField))
        }
      }

      // 应用validFields中的所有字段修改
      for (const field of validFields) {
        if (field.filed && field.value !== undefined) {
          this.setJobValue(field.filed, field.value, UICustomConstants.SEA_BOOKING_BASIC, targetJob)
        }
      }

      // 应用清空状态中的修改
      for (const fieldName in this.clearStates) {
        if (this.clearStates[fieldName] === true) {
          if (this.isMultiFieldComponent(fieldName)) {
            // 处理多字段组件清空：将所有子字段设置为null
            const subFields = this.getMultiFieldSubFields(fieldName)
            subFields.forEach(subField => {
              this.setJobValue(subField, null, UICustomConstants.SEA_BOOKING_BASIC, targetJob)
            })
          } else if (!processedSubFields.has(fieldName)) {
            // 处理普通字段清空，但跳过已经被多字段组件处理的子字段
            const clearValue = (this.isDateField(fieldName) || this.isNumberField(fieldName)) ? null : ''
            this.setJobValue(fieldName, clearValue, UICustomConstants.SEA_BOOKING_BASIC, targetJob)
          }
        }
      }
    },

    async saveBatchJobs(validFields) {
      try {
        this.loading = getLoading4Cxt()
        const lob = getBusinessType(this.formConfig.businessType)?.lob || 'SE'
        const jobListName = this.JOB_LIST_NAMES[lob] || 'seJobList'
        const jobsToSave = []
        const loadJobRelatedFunc = this.isAe ? loadAirExportJobRelated : loadSeaExportJobRelated

        // 步骤 1: 并行获取所有待办任务的完整、最新的数据
        console.log('[批量保存] 开始获取所有任务的完整数据...')
        const loadPromises = this.jobs.map(job => loadJobRelatedFunc({ job: true, jobUuid: job.uuid }))
        const fullJobResponses = await Promise.all(loadPromises)
        const fullJobs = fullJobResponses.map(res => {
          if (!res.data || !res.data.job) {
            throw new Error(`Failed to load full data for a job.`)
          }
          return res.data.job
        })

        // 步骤 2: 遍历完整的任务数据，为每一个任务调用同步接口
        for (const fullJob of fullJobs) {
          const syncRequest = {
            channel: 1,
            branchId: this.$store.getters.currentOfficeId,
            lob: lob,
            jobList: [{
              jobNo: fullJob.jobNo,
              lob: lob,
              validFields: validFields.filter(f => !f.canNotUpdate)
            }],
            needReturnJob: true,
            editableJobStates: this.editableJobStates
          }

          // 关键修复：使用刚刚获取的完整 fullJob 对象作为上下文，并应用用户的修改
          const jobToSync = cloneDeep(fullJob)

          // 应用用户的修改到完整的job数据中
          this.applyUserModificationsToJob(jobToSync, validFields)
          if (this.isSe) {
            jobToSync.poItemsList = jobToSync.poItems
            delete jobToSync.poItems
          }
          cleanCircleObject(jobToSync)
          syncRequest[jobListName] = [jobToSync]

          const syncResponse = await uiCustomUpdateFields(syncRequest)

          if (syncResponse && syncResponse.data && syncResponse.data.jobList && syncResponse.data.jobList[0]) {
            const syncedJob = syncResponse.data.jobList[0]
            if (this.isSe) {
              syncedJob.poItemsList = syncedJob.poItems
              delete syncedJob.poItems
            }
            cleanCircleObject(syncedJob)
            jobsToSave.push(syncedJob)
          } else if (syncResponse && syncResponse.ok) {
            // API 调用成功但没有返回 jobList，使用已修改的完整数据
            const fallbackJob = cloneDeep(jobToSync)
            cleanCircleObject(fallbackJob)
            jobsToSave.push(fallbackJob)
          } else {
            // 如果同步失败，则抛出错误，停止整个流程
            console.error(`[批量保存] Job [${fullJob.jobNo}] 同步失败，响应:`, syncResponse)
            throw new Error(`Failed to sync data for job ${fullJob.jobNo}`)
          }
        }

        // 步骤 3: 构建批量保存请求
        const saveRequest = {
          channel: 1,
          branchId: this.$store.getters.currentOfficeId,
          lob: lob,
          location: LOG_BOOKING_CUSTOM_FORM.code,
          locationParams: 'json:' + JSON.stringify({ customFormUuid: this.formConfig.uuid }),
          editableJobStates: this.editableJobStates
        }
        saveRequest[jobListName] = jobsToSave

        const saveResponse = await uiCustomSave(saveRequest)
        if (saveResponse.ok) {
          const successCount = saveResponse.data.successJobList?.length || 0
          const failedCount = this.jobs.length - successCount
          const message = this.$t('CargoMsg.Cargo_JobsSavedSuccessfully', [successCount])
          if (failedCount > 0) {
            this.$message.warning(this.$t('CargoMsg.Cargo_JobsFailedToSave', [failedCount]))
          } else {
            this.$message.success(message)
          }
          this.$emit('saveSuccess')
          this.$emit('close')
        } else {
          this.$message.error(saveResponse.data.msg || this.$t('CargoMsg.Cargo_BatchSaveFailed'))
        }
      } catch (error) {
        console.error('批量保存失败:', error)
        // error.message 就是我们自己抛出的 'Failed to sync data for job ...'
        this.$message.error(this.$t('CargoMsg.Cargo_BatchSaveFailed') + ': ' + error.message)
      } finally {
        if (this.loading) {
          this.loading.close()
        }
        this.validationRequest = null
        this.validationResult = null
        this.saveAfterValidate = false
      }
    },
    async handleValidationClose() {
      this.showValidationDialog = false
      const validFields = this.validationResult?.data?.jobList?.[0]?.validFields

      // 检查验证结果 - 包括全局错误和字段错误
      if (
        !this.saveAfterValidate ||
        this.validationResult?.data?.jobList.some(j => j.messageKey) || // 检查任一job是否有全局错误
        this.validationResult?.data?.jobList.some(j => j.validFields?.some(field => field.messageKey)) // 检查任一job是否有字段错误
      ) {
        this.validationRequest = null
        this.validationResult = null
        this.saveAfterValidate = false
      }

      if (!this.saveAfterValidate) {
        return
      }

      await this.saveJob(validFields)
    },
    isCsnInfoPagePartType(blockType) {
      return isCsnInfoPagePartType(blockType)
    },
    // 处理组件变更
    handleComponentChange(pagePart, changes) {
      if (!Array.isArray(changes)) {
        return
      }
      if (pagePart.blockType === PAGE_PART_TYPE.AE_CARGO_INFO.value) {
        for (const change of changes) {
          this.setJobValue(change.key, change.value, UICustomConstants.AIR_BOOKING_BASIC)
        }
      }
    },
    // 处理空出Mawb组件变更
    handleAeMawbComponentChange(changes) {
      const mawbDoc = this.findAeDoc(this.syncJobFull, CargoDocConsts.MAWB)
      if (!mawbDoc) {
        return
      }
      for (const change of changes) {
        mawbDoc[change.key] = change.value
      }
    },
    // 处理收发通组件变更
    handleCsnComponentChange(pagePart, changes) {
      // 收发通组件会发出一个包含多个字段变更的数组
      if (!Array.isArray(changes)) {
        return
      }

      if (pagePart.blockType === PAGE_PART_TYPE.CSN_INFO_SE_BASIC.value) {
        for (const change of changes) {
          this.setJobValue(change.key, change.value, UICustomConstants.SEA_BOOKING_BASIC)
        }
      }

      if (pagePart.blockType === PAGE_PART_TYPE.CSN_INFO_SE_MBL.value) {
        for (const change of changes) {
          this.setJobValue(change.key, change.value, UICustomConstants.SEA_BOOKING_MBL)
        }
      }

      if (pagePart.blockType === PAGE_PART_TYPE.CSN_INFO_SE_HBL.value) {
        for (const change of changes) {
          this.setJobValue(change.key, change.value, UICustomConstants.SEA_BOOKING_HBL)
        }
      }

      if (pagePart.blockType === PAGE_PART_TYPE.CSN_INFO_SE_HBL2.value) {
        for (const change of changes) {
          this.setJobValue(change.key, change.value, UICustomConstants.SEA_BOOKING_VIRTUAL_HBL)
        }
      }

      if (pagePart.blockType === PAGE_PART_TYPE.CSN_INFO_AE_BASIC.value) {
        for (const change of changes) {
          this.setJobValue(change.key, change.value, UICustomConstants.AIR_BOOKING_BASIC)
        }
      }
    },
    handleAlsoNotifyChange(pagePart, value) {
      if (pagePart.blockType === PAGE_PART_TYPE.CSN_INFO_SE_BASIC.value) {
        this.setJobValue('alsoNotify', value, UICustomConstants.SEA_BOOKING_BASIC)
      }
      if (pagePart.blockType === PAGE_PART_TYPE.CSN_INFO_SE_HBL.value) {
        this.setJobValue('alsoNotify', value, UICustomConstants.SEA_BOOKING_HBL)
      }
      if (pagePart.blockType === PAGE_PART_TYPE.CSN_INFO_SE_HBL2.value) {
        this.setJobValue('alsoNotify', value, UICustomConstants.SEA_BOOKING_VIRTUAL_HBL)
      }
      if (pagePart.blockType === PAGE_PART_TYPE.CSN_INFO_AE_BASIC.value) {
        this.setJobValue('alsoNotify', value, UICustomConstants.AIR_BOOKING_BASIC)
      }
    },
    checkCsnFlag(pagePart, flag) {
      return pagePart.csnConfig?.[flag]
    },
    getDocTypeByPageType(pageType, fieldName) {
      if (this.isSe) {
        if (pageType === UICustomConstants.SEA_BOOKING_BASIC) {
          if (fieldName === 'placeOfMblPayment') {
            return 'MBL'
          }
          if (fieldName === 'placeOfHblPayment') {
            return 'HBL'
          }
        }
        if (pageType === UICustomConstants.SEA_BOOKING_MBL) {
          if (fieldName === 'typeMBL') {
            return null
          }
          if (fieldName === 'mbl') {
            return null
          }
        }
        if (pageType === UICustomConstants.SEA_BOOKING_HBL) {
          if (fieldName === 'typeHBL') {
            return null
          }
          if (fieldName === 'paymentTermHBL') {
            return null
          }
          if (fieldName === 'soNo') {
            return null
          }
          if (fieldName === 'vessel') {
            return null
          }
          if (fieldName === 'voyageFlight') {
            return null
          }
        }
        if (pageType === UICustomConstants.SEA_BOOKING_VIRTUAL_HBL) {
          if (fieldName === 'typeVirtualHbl') {
            return null
          }
        }
        if (pageType === UICustomConstants.SEA_BOOKING_MBL) {
          return 'MBL'
        } else if (pageType === UICustomConstants.SEA_BOOKING_HBL) {
          return 'HBL'
        } else if (pageType === UICustomConstants.SEA_BOOKING_VIRTUAL_HBL) {
          return 'HAWB2'
        }
      }
      return null
    },
    getActualFieldName(pageType, fieldName) {
      if (this.isSe) {
        if (pageType === UICustomConstants.SEA_BOOKING_BASIC) {
          if (fieldName === 'placeOfMblPayment' || fieldName === 'placeOfHblPayment') {
            return 'paymentPlaceBL'
          }
        }
      } else if (this.isAe) {
        if (pageType === UICustomConstants.AIR_BOOKING_VESSEL) {
          if (fieldName === 'multiJourneyAirline') {
            return 'multipassAirline'
          }
        }
      }
      return fieldName
    },
    findSeaCargoDoc(job, docType, uuid = '') {
      if (!job.jobNo || !job.seaCargoDocs) {
        return null
      }
      const docs = job.seaCargoDocs
      let seaCargoDoc = null
      if (uuid) {
        seaCargoDoc = docs.find(doc => doc.uuid === uuid)
      } else {
        seaCargoDoc = docs.find(doc => doc.docType === docType)
      }
      return seaCargoDoc || null
    },
    findJobSeaCargoDoc(job, docType, uuid = '') {
      if (!job.seaCargoDocs) {
        return null
      }
      const docs = job.seaCargoDocs
      let seaCargoDoc = null
      if (uuid) {
        seaCargoDoc = docs.find(doc => doc.uuid === uuid)
      } else {
        seaCargoDoc = docs.find(doc => doc.docType === docType)
      }
      return seaCargoDoc || null
    },
    findAllSeaCargoDoc(job, docType) {
      if ((!job.jobNo && !this.isBatchMode) || !job.seaCargoDocs) {
        return []
      }
      return job.seaCargoDocs
        .filter(doc => doc.docType === docType)
        .sort((a, b) => (a.hblNbr2 && b.hblNbr2 ? a.hblNbr2.localeCompare(b.hblNbr2) : 0))
    },
    findSeaCargoDocExt(docType) {
      // 批量模式下没有jobFull，但仍需要处理seaCargoDocs
      const hasJobInfo = this.isBatchMode || (this.jobFull && this.jobFull.jobNo)
      if (!hasJobInfo || !this.job.seaCargoDocs) {
        return null
      }

      if (docType === 'MBL') {
        const seaCargoDoc = this.job.seaCargoDocs.find(doc => doc.docType === docType)
        return seaCargoDoc ? seaCargoDoc.mbl || seaCargoDoc.uuid : ''
      } else if (docType === 'HBL' || docType === 'HAWB2') {
        const seaCargoDoc = this.job.seaCargoDocs.find(doc => doc.docType === docType)
        return seaCargoDoc ? seaCargoDoc.hblNbr2 || seaCargoDoc.uuid : ''
      }
      return null
    },
    // 针对字段修改接口的一个特殊设定，对于提单字段，要加"doc_"前缀并传入对应提单号
    convertModifyField(modifyField) {
      const element = this.elementMap[modifyField.filed]
      if (!element || !element.pageType) {
        return modifyField
      }

      const pageType = element.pageType
      if (!this.getDocTypeByPageType(pageType, modifyField.filed)) {
        return modifyField
      }

      const docType = this.getDocTypeByPageType(pageType, modifyField.filed)
      if (!docType) {
        return modifyField
      }

      let ext = ''
      if (this.isSe) {
        ext = this.findSeaCargoDocExt(docType)
      }
      if (!ext) {
        return modifyField
      }

      // 只有当以上条件都满足，也就是提单字段，且对应数据存在时
      const convertedModifyField = {
        filed: 'doc_' + this.getActualFieldName(pageType, modifyField.filed),
        value: modifyField.value,
        sort: modifyField.sort,
        ext: ext
      }
      console.log('[调试] 检测到提单字段，转换修改内容', { modifyField, convertedModifyField })

      return convertedModifyField
    },
    isNumberInput(element) {
      // 检查组件类型是否为数字输入框
      const component = this.elementComponentMap[element.elementName]
      if (component === 'el-input-number') {
        return true
      }

      return false
    },
    isDatePicker(element) {
      // 检查组件类型是否为日期选择器
      const component = this.elementComponentMap[element.elementName]
      return component === 'el-date-picker'
    },
    isDatePickerShowWeek(element) {
      // 检查组件类型是否为带星期的日期选择器
      const component = this.elementComponentMap[element.elementName]
      return component?.name === 'DatePickerShowWeek'
    },
    // 检查字段是否为日期类型（包含所有日期组件）
    isDateField(fieldName) {
      const element = this.elementMap[fieldName]
      if (!element) return false
      return this.isDatePicker(element) || this.isDatePickerShowWeek(element)
    },
    // 检查字段是否为数值类型
    isNumberField(fieldName) {
      const element = this.elementMap[fieldName]
      if (!element) return false
      return this.isNumberInput(element)
    },
    // 获取多字段组件的子字段（人工配置）
    getMultiFieldSubFields(fieldName) {
      const multiFieldMap = {
        'quantityConsignActual': ['quantityContract', 'quantityActual'],
        'weightConsignActual': ['weightContract', 'weightActual'],
        'cubageConsignActual': ['cubageContract', 'cubageActual']
      }
      return multiFieldMap[fieldName] || []
    },
    // 检查字段是否为多字段组件
    isMultiFieldComponent(fieldName) {
      return this.getMultiFieldSubFields(fieldName).length > 0
    },
    handleWritebackJob(data) {
      console.log('[文件历史] 写回数据:', data)

      // TODO: 不同业务类型
      let pageType = 0
      if (this.isSe) {
        pageType = UICustomConstants.SEA_BOOKING_BASIC
      } else if (this.isAe) {
        pageType = UICustomConstants.AIR_BOOKING_BASIC
      }
      for (const key in data) {
        if (this.SKIP_JOB_FIELDS.includes(key)) {
          continue
        }
        this.setJobValue(key, data[key], pageType)
      }
    },
    // 匹配所属分公司
    matchOwnerBranch(row) {
      // TODO: 暂未处理协作分公司逻辑
      if (row.opOffice !== this.$store.getters.currentOfficeId && row.salesOffice !== this.$store.getters.currentOfficeId) {
        return false
      }
      return true
    },
    async initializeContexts() {
      // 按照业务类型设置context
      if (this.formConfig.businessType === BUSINESS_TYPE.AIR_EXPORT.value) {
        // 初始化空出基础上下文
        this.baseAirExportContext = AeBookingNewContext.getInstance()
        await this.baseAirExportContext.prepareData()

        // 初始化空出工作区上下文
        this.airExportContext = new AeBookingWorkspaceContext(this.baseAirExportContext)
        await this.airExportContext.prepareData()

        this.context = this.airExportContext

        JobUtils.initImperialSetting().then(r => {
          this.showImperialButton = r
        })
        JobUtils.initImperialData(SystemConstants.IMPERIAL_1.code).then(r => {
          this.openImperial = r
        })
      } else {
        // 初始化基础上下文
        this.baseSeaExportContext = BookingNewContext.getInstance()
        await this.baseSeaExportContext.prepareData()

        // 初始化海出工作区上下文
        this.seaExportContext = new BookingWorkspaceContext(this.baseSeaExportContext)
        await this.seaExportContext.prepareData()

        this.context = this.seaExportContext
      }

      await loadElementComponentDict()

      console.log('[调试] 上下文初始化完成', this.context)
    },
    // 挂载特殊组件，通过$ref方式调用
    async handleComponentMounted(refName) {
      console.log('[调试] 特殊组件已挂载', refName)
      setTimeout(async() => {
        const component = this.$refs[refName][0]
        if (this.jobFull.jobNo && component) {
          console.log('[调试] 显示特殊组件', { job: this.job, component })
          await component.onShow(this.job, true)
        }
      }, this.COMPONENT_MOUNTED_DELAY)
    },
    closePreviewDialog() {
      this.previewFileData.pdfUrl = ''
      this.previewFileData.pdfSrc = ''
      this.previewFileData.numPages = ''
      this.previewFileData.photoList = []
      this.previewFileData.officeFileUrl = ''
      this.previewFileData.showViewBtn = false
      this.previewFileData.visible = false
    },
    previewFileMethod({ visible, pdfUrl, pdfSrc, numPages, photoList, officeFileUrl }) {
      if (visible) {
        this.previewFileData.pdfUrl = pdfUrl
        this.previewFileData.pdfSrc = pdfSrc
        this.previewFileData.numPages = numPages
        this.previewFileData.photoList = photoList
        this.previewFileData.officeFileUrl = officeFileUrl
        this.previewFileData.visible = true
      } else {
        this.closePreviewDialog()
      }
    },
    // 处理费用锁定状态变更
    chargeLockStatusChange(lastJob) {
      if (!lastJob) {
        return
      }

      let pageType = 0
      if (thius.isAe) {
        pageType = UICustomConstants.AIR_BOOKING_BASIC
      } else {
        pageType = UICustomConstants.SEA_BOOKING_BASIC
      }

      // 更新job对象的费用锁定状态
      this.setJobValue('receivableChargeLockedStatus', lastJob.receivableChargeLockedStatus, pageType)
      this.setJobValue('payableChargeLockedStatus', lastJob.payableChargeLockedStatus, pageType)
      this.setJobValue('receivableChargeLockedDate', lastJob.receivableChargeLockedDate, pageType)
      this.setJobValue('payableChargeLockedDate', lastJob.payableChargeLockedDate, pageType)
    },
    // 获取保函状态
    async updateGuaranteeStateInfo() {
      const hblDoc = this.findSeaCargoDoc(this.job, '', this.selectedPrintDocument)
      if (hblDoc && hblDoc.uuid) {
        try {
          const guaranteeStateInfo = await getGuaranteeStateInfo({ cargoDocUuid: hblDoc.uuid }).then(res => res.data)
          if (guaranteeStateInfo) {
            this.longTermGuaranteeState = guaranteeStateInfo.longTermGuaranteeState
            this.singleTicketGuaranteeState = guaranteeStateInfo.singleTicketGuaranteeState
            this.guaranteeConfigEnable = guaranteeStateInfo.configEnable
            hblDoc.longTermGuaranteeState = guaranteeStateInfo.longTermGuaranteeState
            hblDoc.singleTicketGuaranteeState = guaranteeStateInfo.singleTicketGuaranteeState
            hblDoc.guaranteeConfigEnable = guaranteeStateInfo.configEnable
          }
        } catch (error) {
          // 保函状态获取失败，忽略
          console.log('[调试] 保函状态获取失败', error)
        }
      }
    },
    // 正本/电放打印
    async originalPrint() {
      const config = this.context.config
      // 获取保函状态 - 为当前要打印的文档获取保函状态
      await this.updateGuaranteeStateInfo()
      // 使用选中的文档，如果没有选中则使用HBL作为默认
      const form = this.findSeaCargoDoc(this.job, '', this.selectedPrintDocument)
      if (!form) {
        this.$message.warning(this.$t('CargoMsg.Cargo_BlDataNotFound'))
        return
      }
      // 校验放单状态
      if (Number(config.get('CheckHBLReleaseStateBeforePrint')) === 1) {
        if (form.releaseState !== 1) {
          this.$message.info(this.$t('Message.CheckHBLReleaseStateBeforePrint'))
          return
        }
      }
      // 校验保函
      if (form.docType !== 'HAWB2' && form.releaseType !== BillReleaseType.ORIGINAL.code && this.guaranteeConfigEnable &&
        this.singleTicketGuaranteeState === SystemConstants.SINGLE_TICKET_GUARANTEE_STATE_NOT_UPLOADED.code &&
        this.longTermGuaranteeState !== SystemConstants.LONG_TERM_GUARANTEE_STATE_INEFFECT.code) {
        this.$message.warning(this.$t('Message.PleaseUploadReleaseGuarantee'))
        return
      }
      // 校验流水号
      if (this.originalPrintCheckSerialNo && form.releaseType === 1 && !form.serialNo) {
        this.$message.info(this.$t('Message.PleaseInputBlSerialNumberFirst'))
        return
      }
      // 查找cargoDoc uuid
      const dataObj = form
      const data = {
        uuid: dataObj.uuid,
        state: 1,
        setting: 0,
        btnType: AutomaticReleaseConstants.HBL_ORIGINAL_PRINTING.code
      }
      this.loading = getLoading()
      let res
      try {
        res = await manualTriggerAndUpate(data)
      } finally {
        this.loading.close()
      }
      if (res && res.ok) {
        if (res.data.result === 2) {
          // 额度超限等特殊提示
          const messages = []
          const createElement = this.$createElement
          messages.push(createElement('p', null, this.$t('ReleaseNotes.lineCreditDialog.businessNumber') + res.data.jobNo))
          if (res.data.mbl) messages.push(createElement('p', null, this.$t('ReleaseNotes.lineCreditDialog.mblNo') + res.data.mbl))
          if (res.data.hbl) messages.push(createElement('p', null, this.$t('ReleaseNotes.lineCreditDialog.hblNo') + res.data.hbl))
          if (res.data.tip && res.data.tip.accountCompanyName) messages.push(createElement('p', null, this.$t('ReleaseNotes.lineCreditDialog.partners') + res.data.tip.accountCompanyName))
          if (res.data.tip && res.data.tip.accountOverCreditLimitDate) messages.push(createElement('p', null, this.$t('ReleaseNotes.lineCreditDialog.paymentDays').replace('{0}', this.$t('SeaExport.Exceeded'))))
          if (res.data.tip && res.data.tip.accountOverCreditLimitAmount) messages.push(createElement('p', null, this.$t('ReleaseNotes.lineCreditDialog.lines').replace('{0}', res.data.tip.accountOverCreditLimitAmount)))
          if (res.data.tip && res.data.tip.agentCompanyName) messages.push(createElement('p', null, this.$t('ReleaseNotes.lineCreditDialog.partners') + res.data.tip.agentCompanyName))
          if (res.data.tip && res.data.tip.agentOverCreditLimitDate) messages.push(createElement('p', null, this.$t('ReleaseNotes.lineCreditDialog.paymentDays').replace('{0}', this.$t('SeaExport.Exceeded'))))
          if (res.data.tip && res.data.tip.agentOverCreditLimitAmount) messages.push(createElement('p', null, this.$t('ReleaseNotes.lineCreditDialog.lines').replace('{0}', res.data.tip.agentOverCreditLimitAmount)))
          if (res.data.tip && res.data.tip.customerCompanyName) messages.push(createElement('p', null, this.$t('ReleaseNotes.lineCreditDialog.partners') + res.data.tip.customerCompanyName))
          if (res.data.tip && res.data.tip.customerOverCreditLimitDate) messages.push(createElement('p', null, this.$t('ReleaseNotes.lineCreditDialog.paymentDays').replace('{0}', this.$t('SeaExport.Exceeded'))))
          if (res.data.tip && res.data.tip.customerOverCreditLimitAmount) messages.push(createElement('p', null, this.$t('ReleaseNotes.lineCreditDialog.lines').replace('{0}', res.data.tip.customerOverCreditLimitAmount)))
          messages.push(createElement('p', null, this.$t('ReleaseNotes.lineCreditDialog.triggerRemind')))
          await this.$confirm('', {
            title: this.$t('ReleaseNotes.lineCreditDialog.title'),
            message: createElement('div', null, messages),
            confirmButtonText: this.$t('ReleaseNotes.lineCreditDialog.cancel'),
            showCancelButton: false,
            showClose: false,
            closeOnClickModal: false,
            closeOnPressEscape: false,
            type: 'warning'
          })
          form.releaseState = 4
          // 如有lblReleaseState等label可更新
          return
        }
        if (res.data.result === 1) {
          this.$message({
            message: this.$t('ReleaseNotes.lineCreditDialog.through'),
            type: 'success',
            customClass: 'messageIndex'
          })
          // 如有job.hblCfmState, form.cfmState, lblCfrState, lblReleaseState等label可更新
          // 这里只能更新form.releaseState = 2
          form.releaseState = 2
        }
      }
      // 打印参数
      const params = {}
      params.cargoDocUuid = form.uuid
      params.DOC_UUID = form.uuid
      params.hblNo = form.hblNbr2
      params.hblRefNo = form.hblRefNo
      params.jobuuid = this.jobFull.uuid
      if (form.docType === 'HBL2' || form.docType === 'HAWB2') {
        params.fileAliasName = form.hblNbr2
      }
      // CARGO-6750，SOP回调cargo失败。UUID中有 /导致url匹配不上了
      params.printCallBackUrl = 'services/printExec/entity/update2/CargoDoc?key=' + encodeURIComponent(form.uuid) + '&fieldCount=1&field1=state&value1=2'

      // 打印模板选择对话框及回调
      const printFunc = () => {
        this.templateSelectorDialog.ctx = new SeaCargoDoc(form)
        this.templateSelectorDialog.category = form.releaseType === 1 ? 'HBLZB' : 'HBLDF'
        this.templateSelectorDialog.data = params
        // 组装打印回调函数
        this.prePrintCallback = null
        this.printedCallback = null
        this.returnMainCallback = this.print_callback
        this.v2CheckCallback = this.v2CheckCallbackFun2
        this.templateSelectorDialog.visible = true
      }
      // 检查规则节点
      const checkNode = form.releaseType === 1 ? CheckRuleConstants.NODE_HBLZB_PRINT.code : CheckRuleConstants.NODE_HBLDF_PRINT.code
      const useCheckRule = Number(config.get('useCheckRule')) === 1
      const hasCheckRule = true // 目前直接true，后续如需可补充
      if (useCheckRule && hasCheckRule && this.checkingManager) {
        this.checkingManager(checkNode, printFunc, null)
      } else {
        printFunc()
      }
    },
    // 打印主回调
    async print_callback(event) {
      if (this.isSe) {
        const form = this.findSeaCargoDoc(this.job, 'HBL')
        if (event && event.action === 'print') {
          // CARGO-39566 正本打印/电放打印，不影响 "提单确认状态"状态 （可做平台优化）
          // this.form.cfmState = CargoDocCfmState.CARGODOC_CFM_STATE_RELEASED.code // 已放单
          form.state = 2
          form.releaseState = ReleaseState.ONSINGLERELEASE.code
          await surrendered({ cargoDocUuid: form.uuid, cfmState: form.cfmState, state: form.state, releaseState: form.releaseState })
            .then(res => {
              if (res.data) {
                Object.assign(form, res.data)
              }
            })
          await postRemotingTracking({ jobUuid: this.jobFull.uuid, eventCode: 'SE-IssueHBL' })
        }
      }
    },
    // V2打印反馈回调
    async v2CheckCallbackFun2(printed, template, entity, params) {
      if (this.isSe) {
        const form = this.findSeaCargoDoc(this.job, 'HBL')
        await postRemotingTracking({ jobUuid: this.jobFull.uuid, eventCode: 'SE-IssueHBL' })
        // 提单状态改为已放单
        if (printed && params && params.event && params.event.action === 'print') {
          // CARGO-39566 正本打印/电放打印，不影响 "提单确认状态"状态 （可做平台优化）
          // this.form.cfmState = CargoDocCfmState.CARGODOC_CFM_STATE_RELEASED.code // 已放单
          form.state = 2
          form.releaseState = ReleaseState.ONSINGLERELEASE.code
          await surrendered({ cargoDocUuid: form.uuid, cfmState: form.cfmState, state: form.state, releaseState: form.releaseState })
            .then(async res => {
              if (res.data) {
                Object.assign(form, res.data)
              }
            })
        } else {
          // 不执行操作
        }
      }
    },
    getHblPrintButtonText() {
      const textOriginal = this.$t('SeaExport.PrintTheOriginalBl')
      const textSurrendered = this.$t('SeaExport.TelexRelease')

      // 根据放单类型显示不同文本
      const form = this.findSeaCargoDoc(this.job, '', this.selectedPrintDocument)
      return !form || form.releaseType === 1 ? textOriginal : textSurrendered
    },
    // 生成提单文档标签
    getBlDocumentLabel(doc) {
      if (doc.docType === 'MBL') {
        return `MBL - ${doc.hblNbr2}`
      } else if (doc.docType === 'HBL') {
        return `HBL - ${doc.hblNbr2}`
      } else if (doc.docType === 'HBL2') {
        return `${this.$t('CargoMsg.Cargo_SplitHBL')} - ${doc.hblNbr2}`
      } else if (doc.docType === 'HAWB2') {
        return `${this.$t('SeaExport.FormalHbl')} - ${doc.hblNbr2}`
      }
      return doc.hblNbr2 || ''
    },
    // 重新加载特定页签相关元素
    reloadElements(pageType) {
      // 遍历所有分块和元素，重新加载特定页签相关元素的数据
      for (const pagePart of this.pageParts) {
        for (const element of pagePart.elements) {
          if (element.pageType === pageType) {
            const elementName = element.elementName
            const value = this.getFormFieldValue(elementName, element.pageType, this.job)
            // 强制更新元素值
            this.$nextTick(() => {
              this.setJobValue(elementName, value, element.pageType)
            })
          }
        }
      }
    },
    eqQuantityChangeHandler() {
      ChargeUtils.eqQuantityChangeHandler(this.job, this.context)
    },
    async initializeComponentMaps() {
      console.log('[数据视图] 开始初始化组件映射')

      // 为页面中的所有元素初始化组件映射
      for (const pagePart of this.pageParts) {
        if (pagePart.elements && pagePart.elements.length > 0) {
          for (const element of pagePart.elements) {
            try {
              const elInfo = await this.eManage.loadElement(element.pageType, element.elementName)
              if (elInfo) {
                this.$set(this.elementComponentMap, element.elementName, elInfo.component)
                continue
              }
              const component = await getElementComponent(element)
              this.$set(this.elementComponentMap, element.elementName, component)
            } catch (error) {
              console.error(`[组件映射] 初始化元素 ${element.elementName} 失败:`, error)
              this.$set(this.elementComponentMap, element.elementName, 'el-input')
            }
          }
        }
      }

      console.log('[数据视图] 组件映射初始化完成:', {
        elementCount: Object.keys(this.elementComponentMap).length
      })
    },
    showFullWidth(element) {
      const e = this.eManageGet(element)
      return e ? !!e.fullWidth : false
    },
    findAeDoc(job, docType) {
      if (this.isAe && job.airCargoDocs && job.airCargoDocs.length > 0) {
        for (const doc of job.airCargoDocs) {
          if (doc.docType === docType) {
            return doc
          }
        }
      }
      return null
    }
  }
}
</script>

<style lang="scss" scoped>
// 预览容器样式
.preview-container {
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

// 预览头部样式
.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  border-bottom: 1px solid #dcdfe6;
}

.header-left {
  display: flex;
  align-items: center;
}

.business-title {
  font-size: 18px;
  font-weight: bold;
}

.readonly-reminder {
  font-size: 12px;
  color: #909399;
  margin-left: 8px;
}

.header-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

// 预览内容区域样式
.preview-content::v-deep {
  flex: 1;
  padding: 16px;
  .el-input-number .el-input input {
    text-align: right;
  }
}

// 预览部分样式
.preview-section {
  margin-bottom: 24px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #f8f9fa;
}

// 部分标题样式
.section-header {
  padding: 12px 16px;
  background-color: #f5f7fa;
  border-bottom: 1px solid #ebeef5;
}

.section-title {
  font-size: 16px;
  font-weight: bold;
}

// 部分内容样式
.section-content {
  padding: 16px;
  overflow-x: auto;
}

// 元素网格布局样式
.element-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

// 元素项样式
.element-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fff;
  transition: all 0.3s;

  &:hover {
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  }

  // 添加全宽样式
  &.full-width {
    grid-column: span 4; // 占据整行（4列）
  }

  .element-label {
    font-size: 14px;
    color: #606266;
    font-weight: 500;

    &.required {
      color: #409EFF;
    }
  }
}

// 预览底部样式
.preview-footer {
  padding: 16px;
  border-top: 1px solid #ebeef5;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

// 错误状态样式
.has-error :deep(.el-input__inner) {
  border-color: #F56C6C;
}

.error-message {
  color: #F56C6C;
  font-size: 12px;
  margin-top: 4px;
}

// 收发通信息分块样式
.csn-basic-layout {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.csn-component {
  width: calc(33.33% - 16px);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.csn-component-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.csn-component-label {
  font-size: 14px;
  font-weight: bold;
}

// 提示样式
.loading-container {
  flex: 1;
  display: flex;
  justify-content: left;
  align-items: center;
  padding: 16px;
}

.loading-text {
  font-size: 14px;
  color: #909399;
}

// 文件历史容器样式
.file-history-container {
  width: 100%;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 5px;

  // 文件历史组件内部样式调整
  :deep(.section.operations) {
    margin: 5px;
  }

  :deep(.cargo-ag-grid) {
    margin: 5px 0;
  }
}

// 应收费用容器样式
.charges-receivable-container {
  width: 100%;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 5px;

  // 费用组件内部样式调整
  :deep(.section.operations) {
    margin: 5px;
  }

  :deep(.cargo-ag-grid) {
    margin: 5px 0;
  }
}

// 应付费用容器样式
.charges-payable-container {
  width: 100%;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 5px;

  // 费用组件内部样式调整
  :deep(.section.operations) {
    margin: 5px;
  }

  :deep(.cargo-ag-grid) {
    margin: 5px 0;
  }
}

// 报关信息容器样式
.customs-info-container::v-deep {
  width: fit-content;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 5px;

  // 报关组件内部样式调整
  .section {
    margin: 5px;
    display: flex;
    flex-direction: column;
    padding: 5px;
    border-radius: 2px;
    flex-shrink: 0;
    background-color: #f0f3f8;
  }

  .title {
    display: inline-block;
    position: relative;
    font-size: 13px;
    padding-left: 15px;
    line-height: 14px;
    margin-bottom: 10px;
  }

  .title::before {
    content: "";
    position: absolute;
    width: 4px;
    top: 0;
    left: 0;
    bottom: 0;
    background-color: #1f75d5;
  }

  .cargo-ag-grid {
    margin: 5px 0;
  }
}

// 操作区分块样式
.operation-area {
  width: 100%;
  border-radius: 4px;
  padding: 5px;
  display: flex;
  justify-content: flex-start;
  gap: 8px;
}

// 文档选择器样式
.document-selector {
  margin-bottom: 16px;
}

.document-selector {
  display: inline-block;
  margin-right: 10px;
}

// 清除数据复选框样式
.clear-checkbox-container {
  margin-top: 5px;
  padding-left: 0;
}

.clear-checkbox {
  font-size: 12px;
  color: #666;

  &:hover {
    color: #409eff;
  }
}

// 被清除状态的输入框样式
::v-deep .element-item.cleared {
  .el-input__inner,
  .el-textarea__inner,
  .el-select .el-input__inner,
  .el-date-editor--date .el-input__inner {
    background-color: #f5f7fa !important;
    color: #999 !important;
  }
}

// 在批量模式下为元素项添加额外间距
.preview-container.batch-mode {
  .element-item {
    margin-bottom: 8px;
  }
}
</style>
